import { CommonModule, DatePipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { firstValueFrom, Observable, of, map, catchError } from "rxjs";
import { GetExtisingPOIdRequest } from "../../../../core/models/orders/getExtisingPOIdRequest";
import { PurchaseOrderIdDto } from "../../../../core/models/orders/purchaseOrderIdDto";
import { SalesOrderHeader } from "../../../../core/models/orders/saleOrderHeader";
import { SalesOrderLine } from "../../../../core/models/orders/saleOrderLine";
import { SaleOrderLineAddressDto } from "../../../../core/models/orders/saleOrderLineAddressDto";
import { SaleOrderResponseDto } from "../../../../core/models/orders/saleOrderResponseDto";
import { SalesOrderLinesRequestDto } from "../../../../core/models/orders/salesOrderLinesRequestDto";
import { SalesOrderNotes } from "../../../../core/models/orders/salesOrderNotes";
import { StockPhrase } from "../../../../core/models/orders/stockPhrase";
import { UpdateSalesOrderLineDto } from "../../../../core/models/orders/updateSalesOrderLineDto";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { LoaderService } from "../../../../core/services/loader.service";
import { OrderLineItemDetailService } from "../../../../core/services/order-line-item-detail.service";
import { OrderLineItemService } from "../../../../core/services/order-line-item.service";
import { SharedService } from "../../../../core/services/shared.service";
import { UserRole } from "../../../../shared/enums/userRoles.enum";
import { OrderLineItemDetailsComponent } from "../../ui/order-line-item-details/order-line-item-details.component";
import { OrderLineDetailComponent } from "../order-line-detail/order-line-detail.component";
import { OrderLineShipToComponent } from "../order-line-ship-to/order-line-ship-to.component";
import { OrderLineVendorComponent } from "../order-line-vendor/order-line-vendor.component";
import { OrderNotesSidebarService } from "../../../../core/services/order-notes-sidebar.service";
import { OrderSummaryService } from "../../../../core/services/order-summary.service";
import { PurchaseOrderSidebarService } from "../../../../core/services/purchase-order-sidebar.service";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { v4 as uuidv4 } from "uuid";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { GetD365DefaultProgramsAndSalesClassificationByClientResponse } from "../../../../core/models/program/getD365DefaultProgramsAndSalesClassificationByClientResponse";
import { AddressCountryRegion } from "../../../../core/models/shared/countryRegionList";
import { Currency } from "../../../../core/models/shared/currencyList";
import { ShippingMethod } from "../../../../core/models/shared/shippingMethod";
import { AddNewClientService } from "../../../../core/services/add-new-client.service";
import { CustomerAccountService } from "../../../../core/services/customer-account.service";
import { ProgramsService } from "../../../../core/services/programs.service";
import { SamplepoListingService } from "../../../../core/services/samplepo-listing.service";
import { EnumCopiedFrom } from "../../../../shared/enums/copied-From.enum";
import { StockPhrasesComponent } from "../../../../shared/components/stock-phrases/stock-phrases.component";
import { ClientPurpose } from "../../../../shared/enums/enumClientPurpose";
import { GetAllVendorsDto } from "../../../../core/models/vendor/getAllVendors";
import { PurchaseOrderSidebarComponent } from "../purchase-order-sidebar/purchase-order-sidebar.component";
import { OrderSummaryComponent } from "../order-summary/order-summary.component";
import { ProfileFormService } from "../../../../core/services/profile-form.service";
import { CheckCurrencyMismatchCallingMethod } from "../../../../core/models/common/checkCurrencyCallingMethod";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { GetUserDto } from "../../../../core/models/users/users/getUserDto";
import { Address } from "../../../../core/models/shared/address";
import { SaleOrderItemLineFormDto } from "../../../../core/models/orders/salesOrderLineItemFormDto";
import { SaleOrderLinesDto } from "../../../../core/models/orders/saleOrderLinesDto";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";
import { ButtonModule } from "primeng/button";
import { ReleasedProduct } from "../../../../core/models/shared/releaseProduct";
import { ReleasedProductVariants } from "../../../../core/models/shared/releasedProductVariants";
import { CardModule } from "primeng/card";
import { CheckCurrencyMismatchRequestDto } from "../../../../core/models/common/checkCurrencyMismatchRequestDto";
import { MISC_RELEASED_PRODUCT } from "../../../../shared/constants/misc-constant";
import { ExternalService } from "../../../../core/services/external.service";
import { GetAllSpecialsDto } from "../../../../core/models/specials/getAllSpecials";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";

@UntilDestroy()
@Component({
  selector: "jni-order-line-item",
  templateUrl: "./order-line-item.component.html",
  styleUrls: ["./order-line-item.component.scss"],
  providers: [DatePipe],
  imports: [
    CommonModule,
    forwardRef(() => ModalStackerComponent),
    OrderLineDetailComponent,
    OrderLineItemDetailsComponent,
    OrderLineShipToComponent,
    OrderLineVendorComponent,
    PurchaseOrderSidebarComponent,
    StockPhrasesComponent,
    OrderSummaryComponent,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    CardModule,
    TableModule,
    TooltipModule,
  ],
})
export class OrderLineItemComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() isFromCdi = false;
  @Input() isFromCdiLineAdd = false;
  @Input() poNumber = "";
  @Input() cdiRecId = 0;
  @Input() linesVendorNumber = "";
  @Input() isModal = false;
  createdBy = "";
  createdDate = "";
  modifiedBy = "";
  modifiedDate = "";
  customerLists: Address[] = [];
  displayPosition = false;
  displayPositionDescription = false;
  displayPositionFactory = false;
  position:
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "topleft"
    | "topright"
    | "bottomleft"
    | "bottomright" = "bottomright";
  closable = false;
  displayModal = false;
  saleOrderItemLine: FormGroup;
  stockPhraseForm: FormGroup;
  currencyList: Currency[] = [];
  releasedProductsList: ReleasedProduct[] = [];
  releasedProductsVariantsList: ReleasedProductVariants[] = [];
  @Input() shippingProductsList: ShippingMethod[] = [];

  vendroAddressBookObject!: GetAllVendorsDto;
  saleOrderId = "";
  orderLineNumber = 0;
  sellCurrency = "";
  lineNumber = 0;
  orderItemLineNumber = 1;
  sellCurrencyStatus = false;
  addressObject!: Address;
  headerObject!: SalesOrderHeader;
  lineId = 0;
  customerId = "";
  shipDate = "";
  inHandDate = "";
  shippingMethod = "";
  vendorList: GetAllVendorsDto[] = [];
  stockListing: StockPhrase[] = [];
  filerStockListing: StockPhrase[] = [];
  userRoleId = 0;
  importUserRights = true;
  customerCheckbox = true;
  vendorCheckbox = true;
  vendorAccountNumber = "";
  displaySidebar = false;
  poAssignmentStatus = false;
  directDeliveryVendor = "";
  purchaseOrderId = "";
  poAssignment = "";
  flag = "";
  netFieldStatus = true;
  showAddress = true;
  saleOrderLine: SaleOrderLineAddressDto[] = [];
  lineStatusD365 = "";
  proposalIdD365 = "";
  customerBillToObject!: {
    partyNumber: string;
    accountNumber: string;
  };
  quickEntyFieldStatus = "";
  vendorId = "";
  saleOrderHeaderStatus = "";
  sellRateValueWithIconStatus = false;
  clientShipToAddressDisabled = false;
  vendorNameShipToAddressDisabled = false;
  userBlockedFromEdit = false;
  salesOrderLine: SalesOrderLine = {} as SalesOrderLine;
  showCommissionsFields = true;
  isEdit = false;
  userObject!: GetUserDto;
  lineCopiedFromInfo = "";
  @Input() viewPoNumber = "";
  orderDisplayModal = false;
  copiedFromSalesOrderId = "";
  showHistory = false;
  saleOrderLineHistory!: SalesOrderLine;
  lineActionResult = "";
  saveToPortal = false;
  onSubmitDone = false;
  selectedLineStatusD365 = "";
  deliverRemainderD365 = 0;
  orderedPurchaseQuantityD365 = 0;
  addSetupIsnetInForeignCurrencyValue = false;
  addSetupNetCurrencyValue = "";
  salesClassificationList: DropDownDto[] = [];
  d365DefaultSalesClassification =
    {} as GetD365DefaultProgramsAndSalesClassificationByClientResponse;
  isLineProposalExist = false;
  workflowId = 0;
  isMelissaValidationIgnore = false;
  isCopiedLineMelissaValidationIgnore = false;
  customerFinancialDimension = "";
  selectedItemNumber = "";
  purchaseOrderIdList: PurchaseOrderIdDto[] = [];
  setupLineCopySalesClassification = 0;
  disableConfigFields = false;
  @Input() orderType = "";

  @Input() saleOrderModalStatus = false;
  @Output() closeEvent = new EventEmitter();
  @Output() closeModalNoRefresh = new EventEmitter();
  @Output() scrollHiddenStatus = new EventEmitter();
  @Output() lineGridRefresh = new EventEmitter<boolean>();
  @Input() isPartiallyInvoicedOrderLines = false;
  @Output() changeDetect = new EventEmitter<boolean>();
  @Input() selectedSaleOrderLine: SaleOrderLineAddressDto[] = [];
  @Input() allSalesOrderLines: SaleOrderLineAddressDto[] = [];
  @Input() addNewLineCase = false;

  state: NavigationExtras = {};
  @ViewChild("lineDetail", { static: false })
  lineDetail!: OrderLineDetailComponent;
  currentSaleOrderLine!: Address;
  @ViewChild("shipTo", { static: false })
  shipTo!: OrderLineShipToComponent;

  @ViewChild("details", { static: false })
  details!: OrderLineItemDetailsComponent;

  @ViewChild("stockPhraseDescription", { static: false })
  stockPhraseDescription!: StockPhrasesComponent;

  @ViewChild("stockPhraseFactory", { static: false })
  stockPhraseFactory!: StockPhrasesComponent;

  @ViewChild("vendor", { static: false })
  vendor!: OrderLineVendorComponent;

  // Vendor Specials
  vendorSpecials: GetAllSpecialsDto[] = [];
  loadingSpecials = false;

  //@Output() sellCurrencyConversionEvent = new EventEmitter<any>();
  @Output() updateSalesOrderLineDtoEvent =
    new EventEmitter<UpdateSalesOrderLineDto>();

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input("saleOrderIdFromModal")
  set saleOrderIdFromModal(value: string) {
    this.saleOrderId = value;
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input("orderLineNumberFromModal")
  set orderLineNumberFromModal(value: number) {
    this.orderLineNumber = value;
    if (this.orderLineNumber) {
      this.isEdit = true;
    }
  }

  @Output() closeSalesOrderLineEvent = new EventEmitter();

  @ViewChild("scrollBottom") private scrollBottom!: ElementRef;

  deliveryAddress = [
    {
      id: 1,
      name: "To Header Ship To",
      status: false,
    },
    {
      id: 2,
      name: "To Vendor",
      status: false,
    },
    {
      id: 3,
      name: "Client Address Book",
      status: false,
    },
    {
      id: 4,
      name: "One Time",
      status: false,
    },
    {
      id: 5,
      name: "One Time Blank",
      status: false,
    },
  ];

  formErrors: Record<string, string> = {
    duplicateVendorDirectDelivery: "",
    itemNumberD365: "",
    orderedPurchaseQuantityD365: "",
    vendorPoAssignment: "",
    shipDate: "",
    inHandDate: "",
    addressSelectionName: "",
    deliveryNameD365: "",
    salesClassificationD365: "",
    size: "",
    color: "",
    config: "",
    style: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    duplicateVendorDirectDelivery: {
      required: "Vendor is required.",
    },
    itemNumberD365: {
      required: "Category is required.",
    },
    orderedPurchaseQuantityD365: {
      required: "Quantity is required.",
    },
    vendorPoAssignment: {
      required: "PO Assignment is required.",
    },
    addressSelectionName: {
      required: "Delivery Address is required.",
    },
    countryRegionIdD365: {
      required: "Country is required.",
    },
    shipDate: {
      required: "Ship date is required.",
    },
    inHandDate: {
      required: "In-Hands date is required.",
    },
    deliveryNameD365: {
      maxlength: "Name must be less than 100 characters.",
    },
    salesClassificationD365: {
      required: "Sales Classification is required.",
    },
    color: {
      required: "Color is required.",
    },
    size: {
      required: "Size is required.",
    },
    config: {
      required: "Config is required.",
    },
    Style: {
      required: "Style is required.",
    },
    inventoryItemNumberD365: {
      required: "Item Number is required.",
    },
  };
  //EnumStockPhraseType: any;
  isProgramDimension = false;
  programId = 0;
  isSalesClassificationIdRequired = false;
  validateLoginObject = {} as ValidateLogin;
  countryRegionList: AddressCountryRegion[] = [
    {
      addressFormatD365: "0008",
      brazilCentralBankCountryCodeD365: "",
      countryRegionD365: "USA",
      createdBy: "",
      createdDate: "",
      currencyCodeD365: "",
      id: 0,
      isActive: true,
      isOcodeD365: "US",
      lastModifiedBy: "",
      lastModifiedDate: "",
      modifiedBy1D365: "",
      modifiedDateTime1D365: "",
      oksmCode_RUD365: "",
      parentCountryRegionD365: "",
      timeZoneD365: "",
      useZipPlus4D365: "",
      countryNameD365: "United Sta",
    },
  ];
  @Input() saleOrderData: SaleOrderResponseDto | null = null;

  constructor(
    private _formBuilder: FormBuilder,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService,
    private route: Router,
    private router: ActivatedRoute,
    private _orderLineItem: OrderLineItemService,
    private _samplepoListingService: SamplepoListingService,
    private _addNewClientService: AddNewClientService,
    private _orderLineItemDetailService: OrderLineItemDetailService,
    private _customerAccountService: CustomerAccountService,
    public datepipe: DatePipe,
    public _orderSummaryService: OrderSummaryService,
    public _loginService: UserManagementService,
    public _profileFormService: ProfileFormService,
    private _programService: ProgramsService,
    private _sharedService: SharedService,
    public _orderNotesSidebarService: OrderNotesSidebarService,
    private _purchaseOrderSidebarService: PurchaseOrderSidebarService,
    private _externalService: ExternalService
  ) {
    this.saleOrderItemLine = this._formBuilder.group({
      vendor: this._formBuilder.group({
        duplicateVendorDirectDelivery: [""],
        vendorDirectDelivery: [""],
        vendorPoAssignment: ["", Validators.required],
        vendorName: [""],
        vendorAddress: [""],
        vendorPhone: [""],
        vendorEmail: [""],
        vendorFax: [""],
      }),
      shipTo: this._formBuilder.group({
        deliveryAddress: [""],
        attention: [""],
        countryRegionIdD365: ["", Validators.required],
        zipCodeD365: [{ value: "", disabled: true }, Validators.required],
        cityD365: [{ value: "", disabled: true }],
        stateD365: [{ value: "", disabled: true }],
        addressD365: [""],
        streetD365: [""],
        purposeD365: ClientPurpose.Business,
        isPrimaryD365: "1",
        addressBook: [""],
        buildingComplimentD365: [""],
        apartment_RUD365: [""],
        postBoxD365: [""],
        countyD365: [""],
        streetNumberD365: [""],
        districtNameD365: [""],
        shipToEmailAddress: [""],
        addressSelectionName: ["", Validators.required],
        locationD365: [""],
        addressLocationNumber: [0],
        shipToVendorAccountD365: [""],
        shipToVendorAccountNameD365: [""],
        validateAddress: [""],
        addressFormat: [""],
        rSMJNIShipToEmailD365: [""],
        attentionInformationD365: [""],
        melissaAddressKeyD365: [""],
        addressLine1: [""],
        addressLine2: [""],
        addressLine3: [""],
        deliveryNameD365: ["", Validators.maxLength(100)],
      }),
      detail: this._formBuilder.group({
        shippingMethod: [""],
        accountNumberD365: [""],
        shipDate: [""],
        inHandDate: ["", Validators.required],
        firm: [false],
      }),
      lines: this._formBuilder.group({
        itemNumberD365: ["", Validators.required],
        externalItemNumberD365: [""],
        orderedPurchaseQuantityD365: ["", Validators.required],
        isnetInForeignCurrencyStatus: [false],
        purchasePriceD365: [0],
        netTotalD365: [0],
        netConversion: [""],
        netConversionTotal: [""],
        sell: [0],
        sellTotal: [""],
        sellConversion: [""],
        sellConversionTotal: [""],
        commission: [0],
        grossProfit: [0],
        lineDescriptionD365: [""],
        factoryNotes: [""],
        currencyCodeD365: [""],
        rates: [0],
        locationD365: [""],
        attentionInformationD365: [""],
        lineRecIdD365: [0],
        salesClassificationD365: ["", Validators.required],
        nickName: [""],
        color: [""],
        size: [""],
        config: [""],
        style: [""],
        isTrackable: [true],
        categoryName: [{ value: "", disabled: true }],
      }),
    });

    this.stockPhraseForm = this._formBuilder.group({
      id: [0],
      visibility: ["", Validators.required],
      type: ["", Validators.required],
      customerAccountNumber: [""],
      vendorAccountNumber: [""],
      description: [""],
      notes: [""],
      status: ["none"],
    });

    this.router.params.subscribe(params => {
      this.saleOrderId = params["id"];
      this.orderLineNumber = parseInt(params["linenumber"]);
      if (this.orderLineNumber) {
        this.isEdit = true;
      }
    });

    if (this.route.getCurrentNavigation()?.extras?.state?.["viewPoNumber"]) {
      this.viewPoNumber =
        this.route.getCurrentNavigation()?.extras?.state?.["viewPoNumber"];
    }

    if (this.route.getCurrentNavigation()?.extras?.state?.["addNewLineCase"]) {
      this.addNewLineCase =
        this.route.getCurrentNavigation()?.extras?.state?.["addNewLineCase"];
    }

    if (this.route.getCurrentNavigation()?.extras?.state?.["isCdi"]) {
      this.isFromCdi =
        this.route.getCurrentNavigation()?.extras?.state?.["isCdi"];
    } else {
      this.isFromCdi = false;
    }

    if (this.route.getCurrentNavigation()?.extras?.state?.["isCdiLineAdd"]) {
      this.isFromCdiLineAdd =
        this.route.getCurrentNavigation()?.extras?.state?.["isCdiLineAdd"];
    } else {
      this.isFromCdiLineAdd = false;
    }

    if (this.route.getCurrentNavigation()?.extras?.state?.["cdiRecId"]) {
      this.cdiRecId =
        this.route.getCurrentNavigation()?.extras?.state?.["cdiRecId"];
    }
    if (
      this.route.getCurrentNavigation()?.extras?.state?.["linesVendorNumber"]
    ) {
      this.linesVendorNumber =
        this.route.getCurrentNavigation()?.extras?.state?.["linesVendorNumber"];
    }
    if (
      this.route.getCurrentNavigation()?.extras?.state?.[
        "isPartiallyInvoicedOrderLines"
      ]
    ) {
      this.isPartiallyInvoicedOrderLines =
        this.route.getCurrentNavigation()?.extras?.state?.[
          "isPartiallyInvoicedOrderLines"
        ];
    }

    if (this.route.getCurrentNavigation()?.extras?.state?.["orderType"]) {
      this.orderType =
        this.route.getCurrentNavigation()?.extras?.state?.["orderType"];
    }

    this.state = {
      state: {
        orderType: this.orderType,
      },
    } as NavigationExtras;

    this.setInventorySalesLogic();
  }

  ngOnInit(): void {
    this._sharedService.getSampleWorkflow().subscribe(id => {
      this.workflowId = id;
    });

    this.getUserProfile();
    if (this.saleOrderId) {
      if (this.isFromCdiLineAdd && this.isFromCdi) {
        this.cdiEditCaseDisableFields();
        this.saleOrderItemLine.get(["detail", "inHandDate"])?.enable();
        this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.enable();
      } else if (this.isFromCdi) {
        this.cdiEditCaseDisableFields();
      } else {
        this.disabledVendorDetailAndLineFields();
      }
      //this.getCurrencyListData();
      this.getAllReleaseProduct();
      //this.getAllVariants();
      //this.patchValueInLineItemDate();
      //this.getCountryRegionListData();
      this.getStateOfCollapseAll();

      this.saleOrderItemLine
        .get(["lines", "isnetInForeignCurrencyStatus"])
        ?.disable();
      this.IsUserBlockedFromEdit();
      this.checkShowHideFields();
      this.getAllSalesClassifications();
      //this.getSaleOrderData();
    }
  }

  setInventorySalesLogic() {
    if (this.orderType == "inventory-sales" && !this.orderLineNumber) {
      this.poAssignment = "Default";
      this.saleOrderItemLine.patchValue({
        vendor: {
          vendorPoAssignment: "Default",
        },
      });
    }
  }

  cdiEditCaseDisableFields() {
    this.saleOrderItemLine.get(["vendor", "vendorName"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorAddress"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPhone"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorEmail"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorFax"])?.disable();
    this.saleOrderItemLine
      .get(["vendor", "duplicateVendorDirectDelivery"])
      ?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();

    this.saleOrderItemLine.get(["lines", "commission"])?.disable();
    this.saleOrderItemLine.get(["lines", "grossProfit"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "isnetInForeignCurrencyStatus"])
      ?.disable();
    // for Ticket 11639
    //this.saleOrderItemLine.get(['lines', 'factoryNotes'])?.disable();
    //this.saleOrderItemLine.get(['lines', 'lineDescriptionD365'])?.disable();
    this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.disable();
    //this.saleOrderItemLine.get(['lines', 'externalItemNumberD365'])?.disable();
    this.saleOrderItemLine.get(["lines", "sell"])?.disable();

    this.saleOrderItemLine.get(["detail", "shippingMethod"])?.disable();
    this.saleOrderItemLine.get(["detail", "accountNumberD365"])?.disable();
    this.saleOrderItemLine.get(["detail", "firm"])?.disable();
    this.saleOrderItemLine.get(["detail", "shipDate"])?.disable();
    this.saleOrderItemLine.get(["detail", "inHandDate"])?.disable();

    this.saleOrderItemLine.get(["shipTo", "addressSelectionName"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "deliveryNameD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "attentionInformationD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "countryRegionIdD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "attention"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "locationD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "rSMJNIShipToEmailD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "shipToVendorAccountNameD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressFormat"])?.disable();

    this.netFieldStatus = false;
    this.sellRateValueWithIconStatus = false;
    this.clientShipToAddressDisabled = true;
    this.vendorNameShipToAddressDisabled = true;
  }

  logValidationErrors(group: FormGroup = this.saleOrderItemLine): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          let messages = this.validationMessages[key];
          if (
            this.orderType === "inventory-sales" &&
            key === "itemNumberD365"
          ) {
            messages = this.validationMessages["inventoryItemNumberD365"];
          }
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  IsUserBlockedFromEdit() {
    this._orderLineItem
      .IsUserBlockedFromEdit(this.saleOrderId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.userBlockedFromEdit = res.data;
          if (!this.userBlockedFromEdit) {
            this.saleOrderItemLine
              .get(["lines", "isnetInForeignCurrencyStatus"])
              ?.disable();
            this.disabledFormFieldForInvoiced();
          }
        },
        error: () => {
          this.userBlockedFromEdit = false;
          this.disabledFormFieldForInvoiced();
        },
      });
  }

  patchValueByLineNumber(
    salesOrderLine: SaleOrderLineAddressDto[],
    sellCurrencyStatus: string
  ) {
    if (this.orderLineNumber) {
      const saleOrderLineList = salesOrderLine.filter(
        x => x.salesOrderLine?.id === this.orderLineNumber
      );
      if (saleOrderLineList.length > 0) {
        if (saleOrderLineList[0]?.salesOrderLine) {
          this.lineId = saleOrderLineList[0]?.salesOrderLine.id;
          const lineData = saleOrderLineList[0]?.salesOrderLine;
          this.salesOrderLine = lineData;
          if (saleOrderLineList[0]?.address) {
            this.currentSaleOrderLine = saleOrderLineList[0]?.address;
          }

          this.patchValueInLineForm(
            saleOrderLineList[0]?.salesOrderLine,
            sellCurrencyStatus
          );

          let categoryName = "";
          if (this.salesOrderLine.itemNumberD365 === "Misc") {
            categoryName = "Misc";
            this.releasedProductsList.push(MISC_RELEASED_PRODUCT);
          } else {
            categoryName =
              this.releasedProductsList.find(
                x => x.itemNumberD365 == this.salesOrderLine.itemNumberD365
              )?.nameD365 ?? "";
          }

          this.saleOrderItemLine.patchValue({
            lines: {
              categoryName,
            },
          });

          if (this.lineStatusD365?.toLowerCase() === "invoiced") {
            this.disabledFormFieldForInvoiced();
          }

          if (
            this.lineStatusD365?.toLowerCase() === "delivered" &&
            this.saleOrderHeaderStatus?.toLowerCase() === "delivered"
          ) {
            this.disabledEnabledFormFieldForDelivered();
          }

          if (
            saleOrderLineList[0]?.salesOrderLine &&
            !saleOrderLineList[0]?.salesOrderLine?.addressSelectionNameD365
              ?.toLowerCase()
              ?.includes("header")
          ) {
            this.patchValueInDeliveryName(
              saleOrderLineList[0]?.salesOrderLine.deliveryNameD365 || ""
            );
          }

          this.getSourcingVendor(
            saleOrderLineList[0]?.salesOrderLine?.vendorDirectDeliveryD365 ?? ""
          );

          if (saleOrderLineList[0]?.salesOrderLine.shipDateD365) {
            // const shipDate = saleOrderLineList[0]?.salesOrderLine.shipDateD365;
            // this.shipDate = shipDate;
            const shipDate = new Date(
              saleOrderLineList[0]?.salesOrderLine.shipDateD365
            );
            this.shipDate =
              this.datepipe.transform(shipDate, "MM/dd/yyyy") ?? "";
            this.patchValueInLineShipDate(this.shipDate);
          } else {
            this.shipDate = "";
          }
          if (saleOrderLineList[0]?.salesOrderLine.inHandDateD365) {
            // const inHandDate =
            //   saleOrderLineList[0]?.salesOrderLine.inHandDateD365;
            // this.inHandDate = inHandDate;
            const inHandDate = new Date(
              saleOrderLineList[0]?.salesOrderLine.inHandDateD365
            );
            this.inHandDate =
              this.datepipe.transform(inHandDate, "MM/dd/yyyy") ?? "";
            this.patchValueInLineHandDate(this.inHandDate);
          } else {
            this.inHandDate = "";
          }
          if (saleOrderLineList[0]?.salesOrderLine.shippingMethodD365) {
            this.shippingMethod =
              saleOrderLineList[0]?.salesOrderLine.shippingMethodD365;
            this.patchValueInShippingMethod(this.shippingMethod);
          }
          if (lineData.accountNumberD365) {
            this.patchValueInAccountNumber(lineData.accountNumberD365);
          }
          if (saleOrderLineList[0]?.salesOrderLine.firmD365) {
            this.patchValueInFirm(
              saleOrderLineList[0]?.salesOrderLine.firmD365 === 1 ? true : false
            );
          }
        }

        if (saleOrderLineList[0]?.address) {
          if (
            saleOrderLineList[0]?.salesOrderLine?.addressSelectionNameD365
              ?.toLowerCase()
              .includes("one")
          ) {
            this.showAddress = false;
            this.saleOrderItemLine
              .get(["shipTo", "countryRegionIdD365"])
              ?.enable();
            this.saleOrderItemLine.get(["shipTo", "addressD365"])?.enable();
          }

          if (
            !saleOrderLineList[0]?.salesOrderLine?.addressSelectionNameD365
              ?.toLowerCase()
              .includes("header")
          ) {
            this.patchValueInLineAddressForm(saleOrderLineList[0]?.address);
          }
        }
        this.deliveryAddress[2].status = false;
      }
    }
  }

  patchValueInAccountNumber(accountNumber: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        accountNumberD365: accountNumber,
      },
    });
  }

  patchValueInLineShipDate(date: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        shipDate: date,
      },
    });
  }

  patchValueInLineHandDate(date: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        inHandDate: date,
      },
    });
  }

  applylineDescriptionLengthValidation(event: FocusEvent) {
    const target = event.target as HTMLTextAreaElement;
    if (target.value) {
      this.saleOrderItemLine.patchValue({
        lines: {
          lineDescriptionD365: target.value.substring(0, 2000),
        },
      });
    }
  }

  patchValueInDeliveryName(name: string) {
    this.saleOrderItemLine.patchValue({
      shipTo: {
        deliveryNameD365: name,
      },
    });
  }

  applyfactoryNotesLengthValidation(event: FocusEvent) {
    const target = event.target as HTMLTextAreaElement;
    if (target.value) {
      this.saleOrderItemLine.patchValue({
        lines: {
          factoryNotes: target.value.substring(0, 2000),
        },
      });
    }
  }

  getSourcingVendor(accountNumberOrName: string) {
    this.vendorId = accountNumberOrName;

    if (this.vendorId) {
      this.isVendorSelectedEvent(true);
    }

    if (!accountNumberOrName) {
      return;
    }

    this._samplepoListingService
      .getVendorByNameAndAccountNumberFilter(accountNumberOrName)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.vendorList = res?.data as unknown as GetAllVendorsDto[];
          if (this.vendorList && this.vendorList.length > 0) {
            this.vendroAddressBookObject = this.vendorList[0];
            this.patchValueInVendor(this.vendorList[0]);
            this.patchValueInVendorAccount(accountNumberOrName);
            this.patchValueInVendorAddress(
              this.vendorList[0].partyNumber ?? "",
              accountNumberOrName
            );
            this.getVendorObject(this.vendroAddressBookObject);
          }
        },
      });
  }

  patchValueInVendorAddress(partyNumber: string, accountNumber: string) {
    this._samplepoListingService
      .getVendorAddressByPartyNumber(partyNumber, accountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<Address[]>) => {
          const primaryAddress = res.data.filter(
            x =>
              x.isPrimaryD365?.toLowerCase() === "yes" ||
              x.isPrimaryD365?.toLowerCase() === "1"
          );
          if (primaryAddress.length > 0) {
            if (primaryAddress[0].addressD365) {
              this.patchValuesInVendorFormAddressField(
                primaryAddress[0].addressD365
              );
            }
          }
        },
      });
  }

  patchValuesInVendorFormAddressField(address: string) {
    this.saleOrderItemLine.patchValue({
      vendor: {
        vendorAddress: address,
      },
    });
  }

  patchValueInVendor(vendorObject: GetAllVendorsDto) {
    this.saleOrderItemLine.patchValue({
      vendor: {
        vendorName: vendorObject.vendorName,
        vendorPhone: vendorObject.phoneNumber,
        vendorEmail: vendorObject.emailAddress,
        vendorFax: vendorObject.fax,
      },
    });
  }

  patchValueInVendorAccount(vendorAccount: string) {
    this.saleOrderItemLine.patchValue({
      vendor: {
        vendorDirectDelivery: vendorAccount,
        duplicateVendorDirectDelivery: vendorAccount,
      },
    });
  }

  patchValueInLineForm(
    salesOrderLineObject: SalesOrderLine,
    sellCurrencyStatus: string
  ) {
    if (
      salesOrderLineObject.lineCopiedFrom &&
      salesOrderLineObject.lineCopiedFromHeaderId
    ) {
      this.saleOrderLineHistory = salesOrderLineObject;
      this.showHistory = true;
    }
    this.saleOrderItemLine.patchValue({
      vendor: {
        vendorDirectDelivery: salesOrderLineObject.vendorDirectDeliveryD365,
        vendorPoAssignment: salesOrderLineObject.vendorPoAssignmentD365 ?? "",
      },
      lines: {
        itemNumberD365: salesOrderLineObject.itemNumberD365 ?? "",
        externalItemNumberD365:
          salesOrderLineObject.externalItemNumberD365 ?? "",
        orderedPurchaseQuantityD365:
          salesOrderLineObject.orderedPurchaseQuantityD365 ?? "",
        isnetInForeignCurrencyStatus:
          salesOrderLineObject.isnetInForeignCurrencyStatus === null ||
          this.isFromCdi
            ? false
            : salesOrderLineObject.isnetInForeignCurrencyStatus,
        purchasePriceD365: salesOrderLineObject.purchasePriceD365 ?? "",
        netTotalD365: salesOrderLineObject.netTotalD365 ?? "",
        netConversion: salesOrderLineObject.netConversion ?? "",
        netConversionTotal: salesOrderLineObject.netConversionTotalD365 ?? "",
        sell: salesOrderLineObject.sellD365 ?? "",
        sellTotal: salesOrderLineObject.sellTotalD365 ?? "",
        sellConversion: salesOrderLineObject.sellConversion ?? "",
        sellConversionTotal: salesOrderLineObject.sellConversionTotal ?? "",
        commission: salesOrderLineObject.commissionD365 ?? "",
        grossProfit: salesOrderLineObject.grossProfitD365 ?? "",
        lineDescriptionD365: salesOrderLineObject.lineDescriptionD365 ?? "",
        factoryNotes: salesOrderLineObject.factoryNotesD365 ?? "",
        currencyCodeD365: salesOrderLineObject.currencyCodeD365 ?? "",
        rates: salesOrderLineObject.rates ?? "",
        lineRecIdD365: salesOrderLineObject.lineRecIdD365 ?? "",
        salesClassificationD365:
          salesOrderLineObject.salesClassificationIdD365 === null ||
          salesOrderLineObject.salesClassificationIdD365 === 0
            ? ""
            : salesOrderLineObject.salesClassificationIdD365,
        nickName: salesOrderLineObject.nickName ?? "",
        isTrackable: salesOrderLineObject.isTrackable ?? false,
        size: salesOrderLineObject.size,
        color: salesOrderLineObject.color,
        config: salesOrderLineObject.config,
        style: salesOrderLineObject.style,
      },
    });
    if (
      !salesOrderLineObject?.addressSelectionNameD365
        ?.toLowerCase()
        ?.includes("header")
    ) {
      this.saleOrderItemLine.patchValue({
        shipTo: {
          attentionInformationD365:
            salesOrderLineObject.attentionInformationD365,
          //addressSelectionName: salesOrderLineObject.addressSelectionNameD365,
          locationD365: salesOrderLineObject.locationD365,
          addressLocationNumber: salesOrderLineObject.addressLocationNumberD365,
          shipToVendorAccountD365: salesOrderLineObject.shipToVendorAccountD365,
          rSMJNIShipToEmailD365: salesOrderLineObject.rsmjniShipToEmailD365,
        },
      });
    }
    if (this.lineDetail) {
      this.lineDetail.patchValueInLines(
        salesOrderLineObject,
        sellCurrencyStatus
      );
    }

    if (this.shipTo) {
      this.shipTo.showAddressFieldLineSelection(
        salesOrderLineObject.addressSelectionNameD365 ?? ""
      );
      this.shipTo.getVendorByName(
        salesOrderLineObject.shipToVendorAccountD365 ?? ""
      );
    }

    if (salesOrderLineObject.vendorPoAssignmentD365) {
      this.poAssignment = salesOrderLineObject.vendorPoAssignmentD365;
    }

    this.patchValueInaddressSelection(
      salesOrderLineObject.addressSelectionNameD365 ?? ""
    );
    this.lineStatusD365 = salesOrderLineObject.lineStatusD365 ?? "";
    this.proposalIdD365 = salesOrderLineObject.proposalIdD365 ?? "";

    if (
      this.lineStatusD365 &&
      this.lineStatusD365?.toLowerCase() === "invoiced"
    ) {
      this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();
    }

    if (
      salesOrderLineObject.deliverRemainderD365 !== null &&
      salesOrderLineObject.orderedPurchaseQuantityD365 !== null &&
      salesOrderLineObject.deliverRemainderD365 >
        salesOrderLineObject.orderedPurchaseQuantityD365
    ) {
      this.disabledQuantityField();
    }
  }

  disabledFormFieldForInvoiced() {
    this.saleOrderItemLine
      .get(["vendor", "duplicateVendorDirectDelivery"])
      ?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();

    this.saleOrderItemLine.get(["lines", "factoryNotes"])?.disable();
    this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "orderedPurchaseQuantityD365"])
      ?.disable();
    this.saleOrderItemLine.get(["lines", "externalItemNumberD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "currencyCodeD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "isnetInForeignCurrencyStatus"])
      ?.disable();

    this.saleOrderItemLine.get(["detail", "shippingMethod"])?.disable();
    this.saleOrderItemLine.get(["detail", "accountNumberD365"])?.disable();
    this.saleOrderItemLine.get(["detail", "firm"])?.disable();
    this.saleOrderItemLine.get(["detail", "shipDate"])?.disable();
    this.saleOrderItemLine.get(["detail", "inHandDate"])?.disable();

    this.saleOrderItemLine.get(["shipTo", "addressSelectionName"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "deliveryNameD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "attentionInformationD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "countryRegionIdD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "attention"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "locationD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "rSMJNIShipToEmailD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "shipToVendorAccountNameD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressFormat"])?.disable();

    // Enable those fields as per the ticket 11279
    this.saleOrderItemLine.get(["lines", "sell"])?.disable();
    this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.disable();
    this.sellRateValueWithIconStatus = true; // to make it sell field enable
    this.saleOrderItemLine.get(["lines", "nickName"])?.disable();
    this.netFieldStatus = true;
    this.clientShipToAddressDisabled = true;
    this.vendorNameShipToAddressDisabled = true;
    this.saleOrderItemLine.get(["lines", "salesClassificationD365"])?.disable();
    this.disableConfigFields = true;
  }

  disabledFormFieldForDelivered() {
    this.saleOrderItemLine
      .get(["vendor", "duplicateVendorDirectDelivery"])
      ?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();
    this.saleOrderItemLine.get(["lines", "factoryNotes"])?.disable();
    this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "orderedPurchaseQuantityD365"])
      ?.disable();
    this.saleOrderItemLine.get(["lines", "externalItemNumberD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "currencyCodeD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "isnetInForeignCurrencyStatus"])
      ?.disable();
    this.saleOrderItemLine.get(["lines", "sell"])?.disable();

    this.saleOrderItemLine.get(["detail", "shippingMethod"])?.disable();
    this.saleOrderItemLine.get(["detail", "accountNumberD365"])?.disable();
    this.saleOrderItemLine.get(["detail", "firm"])?.disable();
    this.saleOrderItemLine.get(["detail", "shipDate"])?.disable();
    this.saleOrderItemLine.get(["detail", "inHandDate"])?.disable();

    this.saleOrderItemLine.get(["shipTo", "addressSelectionName"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "deliveryNameD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "attentionInformationD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "countryRegionIdD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "attention"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "locationD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "rSMJNIShipToEmailD365"])?.disable();

    this.saleOrderItemLine
      .get(["shipTo", "shipToVendorAccountNameD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressFormat"])?.disable();
    this.netFieldStatus = true;
    this.sellRateValueWithIconStatus = true;
    this.clientShipToAddressDisabled = true;
    this.vendorNameShipToAddressDisabled = true;
    this.saleOrderItemLine.get(["lines", "salesClassificationD365"])?.disable();
    // if (
    //   salesOrderLineObject.deliverRemainderD365 !== null &&
    //   salesOrderLineObject.orderedPurchaseQuantityD365 !== null &&
    //   salesOrderLineObject.deliverRemainderD365 <
    //     salesOrderLineObject.orderedPurchaseQuantityD365
    // ) {
    //   this.netFieldStatus = false;
    //   this.saleOrderItemLine.get(['lines', 'lineDescriptionD365'])?.enable();
    //   this.saleOrderItemLine.get(['vendor', 'vendorPoAssignment'])?.enable();
    // }
  }

  disabledEnabledFormFieldForDelivered() {
    this.saleOrderItemLine
      .get(["vendor", "duplicateVendorDirectDelivery"])
      ?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();
    this.saleOrderItemLine.get(["lines", "factoryNotes"])?.disable();
    this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "orderedPurchaseQuantityD365"])
      ?.disable();
    this.saleOrderItemLine.get(["lines", "externalItemNumberD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "currencyCodeD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "isnetInForeignCurrencyStatus"])
      ?.disable();

    this.saleOrderItemLine.get(["detail", "shippingMethod"])?.disable();
    this.saleOrderItemLine.get(["detail", "accountNumberD365"])?.disable();
    this.saleOrderItemLine.get(["detail", "firm"])?.disable();
    this.saleOrderItemLine.get(["detail", "shipDate"])?.disable();
    this.saleOrderItemLine.get(["detail", "inHandDate"])?.disable();

    this.saleOrderItemLine.get(["shipTo", "addressSelectionName"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "deliveryNameD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "attentionInformationD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "countryRegionIdD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "attention"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "locationD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "rSMJNIShipToEmailD365"])?.disable();

    this.saleOrderItemLine
      .get(["shipTo", "shipToVendorAccountNameD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressFormat"])?.disable();

    // Enable those fields as per the ticket 11279
    this.saleOrderItemLine.get(["lines", "sell"])?.enable();
    this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.enable();
    this.sellRateValueWithIconStatus = false; // to make it sell field enable

    this.netFieldStatus = true;

    this.clientShipToAddressDisabled = true;
    this.vendorNameShipToAddressDisabled = true;
  }

  disabledQuantityField() {
    this.saleOrderItemLine
      .get(["lines", "orderedPurchaseQuantityD365"])
      ?.disable();
  }

  patchValueInaddressSelection(addressSelectionName: string) {
    let addressBook = "";
    if (addressSelectionName.toLowerCase().includes("address book")) {
      addressBook = "Client Address Book";
    } else if (addressSelectionName.toLowerCase().includes("header ship")) {
      addressBook = "To Header Ship To";
    } else if (addressSelectionName.toLowerCase().includes("vendor")) {
      addressBook = "To Vendor";
    } else if (addressSelectionName.toLowerCase().includes("one time")) {
      addressBook = "One Time";
    } else if (addressSelectionName.toLowerCase().includes("blank")) {
      addressBook = "One Time Blank";
    } else {
      addressBook = addressSelectionName;
    }
    this.saleOrderItemLine.patchValue({
      shipTo: {
        addressSelectionName: addressBook,
      },
    });

    if (!addressBook?.toLowerCase()?.includes("header") && this.shipTo) {
      this.shipTo.enableAttentionandLocationField();
    }
  }

  patchValueInLineAddressForm(salesOrderLineAddressObject: Address) {
    this.saleOrderItemLine.patchValue({
      shipTo: {
        countryRegionIdD365:
          salesOrderLineAddressObject.countryRegionIdD365 === ""
            ? salesOrderLineAddressObject.countryRegionISOCodeD365
            : salesOrderLineAddressObject.countryRegionIdD365,
        zipCodeD365: salesOrderLineAddressObject.zipCodeD365,
        cityD365: salesOrderLineAddressObject.cityD365,
        stateD365: salesOrderLineAddressObject.stateD365,
        streetD365: salesOrderLineAddressObject.streetD365,
        buildingComplimentD365:
          salesOrderLineAddressObject.buildingComplimentD365,
        postBoxD365: salesOrderLineAddressObject.postBoxD365,
        countyD365: salesOrderLineAddressObject.countyD365,
        streetNumberD365: salesOrderLineAddressObject.streetNumberD365,
        districtNameD365: salesOrderLineAddressObject.districtNameD365,
        addressLocationName:
          salesOrderLineAddressObject.addressLocationNameD365,
        addressD365: salesOrderLineAddressObject.addressD365,
      },
    });
  }

  // getCountryRegionListData() {
  //   this._addNewClientService
  //     .getCountryRegionList()
  //     .pipe(untilDestroyed(this))
  //     .subscribe({
  //       next: (res) => {
  //         this.countryRegionList = res?.data;
  //       }
  //     });
  // }

  getVendorObject(event: GetAllVendorsDto) {
    this.vendroAddressBookObject = event;
    if (this.vendroAddressBookObject.vendorAccountNumber) {
      this.vendorCheckbox = false;
      this.vendorAccountNumber =
        this.vendroAddressBookObject.vendorAccountNumber;
      this.directDeliveryVendor = this.vendorAccountNumber;

      // Load vendor specials
      this.loadVendorSpecials(this.vendorAccountNumber);

      //this.getPurchaseOrderId(this.saleOrderId, this.directDeliveryVendor);
    }

    if (
      (this.saleOrderHeaderStatus?.toLowerCase() !== "delivered" &&
        this.saleOrderHeaderStatus?.toLowerCase() !== "invoiced") ||
      (this.lineStatusD365?.toLowerCase() !== "delivered" &&
        this.lineStatusD365?.toLowerCase() !== "invoiced")
    ) {
      if (
        this.userBlockedFromEdit &&
        !this.isFromCdi &&
        this.lineStatusD365.toLowerCase() !== "delivered"
      ) {
        this.saleOrderItemLine
          .get(["lines", "isnetInForeignCurrencyStatus"])
          ?.enable();
      }
      this.netFieldStatus = false;
      if (this.lineDetail) {
        this.lineDetail.enableNetField();
      }
    }
  }

  loadVendorSpecials(vendorAccountNumber: string) {
    if (!vendorAccountNumber) {
      this.vendorSpecials = [];
      return;
    }

    this.loadingSpecials = true;
    this._loaderService.setLoaderState(true);
    this._externalService
      .getSpecialsByVendorAccount(vendorAccountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: GenericResponseDto<GetAllSpecialsDto[]>) => {
          this.loadingSpecials = false;
          this._loaderService.setLoaderState(false);
          if (response.status === "Success" && response.data) {
            this.vendorSpecials = response.data;
          } else {
            this.vendorSpecials = [];
          }
        },
        error: () => {
          this.loadingSpecials = false;
          this._loaderService.setLoaderState(false);
          this.vendorSpecials = [];
        },
      });
  }

  disabledVendorDetailAndLineFields() {
    this.saleOrderItemLine.get(["vendor", "vendorName"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorAddress"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPhone"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorEmail"])?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorFax"])?.disable();
    this.saleOrderItemLine.get(["lines", "commission"])?.disable();
    this.saleOrderItemLine.get(["lines", "grossProfit"])?.disable();
  }

  getVendorAccountField(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    if (target.value != "") {
      this.deliveryAddress[2].status = false;
    } else {
      this.deliveryAddress[2].status = true;
    }
  }

  patchValueInLineItemDate() {
    const currentDate = new Date().toISOString();
    this.saleOrderItemLine.patchValue({
      detail: {
        shipDate: currentDate,
        inHandDate: currentDate,
      },
    });
  }

  getAllVariants() {
    this._samplepoListingService
      .getAllVariants()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.releasedProductsVariantsList = res.data.filter(
            item => item.poStopped === "No"
          );
        },
      });
  }

  getAllReleaseProduct() {
    this._samplepoListingService
      .getAllCategory()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<ReleasedProduct[]>) => {
          this.releasedProductsList =
            this.orderType == "inventory-sales"
              ? res.data.filter(item => item.soStopped === "No")
              : res.data.filter(
                  x => x.itemNumberD365 != "Misc" && x.soStopped === "No"
                );
        },
      });
  }

  getCurrencyListData() {
    this._addNewClientService
      .getCurrencyList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.currencyList = res.data;
        },
      });
  }

  getAllShippingMethod() {
    this._samplepoListingService
      .getAllShippingMethod()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<ShippingMethod[]>) => {
          const shippingData = res.data;
          this.shippingProductsList = shippingData.filter(
            x => x.jniUseOnSalesOrderD365 !== "No"
          );
          if (this.shippingMethod) {
            this.onShippingMethodSelect(this.shippingMethod);
          }
        },
      });
  }

  async redirectToSummary() {
    if (this.saleOrderModalStatus) {
      this.closeModalNoRefresh.emit();
      return;
    }

    if (this.workflowId != 0 && this.workflowId != undefined) {
      this.route.navigateByUrl("/production-workflow", {
        state: { workflowId: this.workflowId, salesOrderId: this.saleOrderId },
      });
      this._sharedService.updateSampleWorkflow(0);
      return;
    } else if (this.isFromCdi) {
      const url = `/cdi/cdi-resolution-summary/${this.cdiRecId}`;
      this.route.navigate([url]);
    } else {
      this.route.navigate(["orders/order-summary/" + this.saleOrderId]);
    }
  }

  getSaleOrderData() {
    if (!this.saleOrderId || !this.saleOrderData) {
      this.getSaleOrderDataFromAPI();
      return;
    }

    this._loaderService.setLoaderState(true);
    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.jniSalesInventoryD365
    ) {
      this.orderType = "inventory-sales";
      this.setInventorySalesLogic();
      this.getAllVariants();
    } else {
      this.orderType = "all-sales";
    }

    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.foreignCurrencyD365
    ) {
      this.sellCurrency =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.foreignCurrencyD365;
    }
    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.foreignCurrencyStatus
    ) {
      this.sellCurrencyStatus =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.foreignCurrencyStatus;
    }
    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.salesOrderStatusD365
    ) {
      this.saleOrderHeaderStatus =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.salesOrderStatusD365;
    }
    if (this.saleOrderData?.salesOrderHeader?.salesOrderHeader) {
      this.customerId =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.customerIdD365 ??
        "";
      if (this.customerId) {
        this.customerCheckbox = false;

        // get default sales classification if comes from D365
        this.getD365DefaultProgramsAndSalesClassificationByClient(
          this.customerId
        );
        this.getCustomerFinancialDimension(this.customerId);
      }
    }
    if (
      (this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.approvalStatusD365 == null ||
        this.saleOrderData?.salesOrderHeader?.salesOrderHeader
          ?.approvalStatusD365 == "" ||
        this.saleOrderData?.salesOrderHeader?.salesOrderHeader?.approvalStatusD365.toLowerCase() ==
          "none") &&
      this.vendor
    ) {
      this.vendor.poAssignmentStatus();
    }

    if (
      !this.quickEntyFieldStatus?.includes("Copy Header") &&
      !this.quickEntyFieldStatus?.includes("Copy Line") &&
      !this.quickEntyFieldStatus?.includes("Add Setup") &&
      !this.quickEntyFieldStatus?.includes("Misc")
    ) {
      if (
        this.saleOrderData?.salesOrderHeader?.address &&
        !(
          this.saleOrderData?.salesOrderHeader?.salesOrderHeader?.addressSelectionD365
            ?.toLowerCase()
            .includes("multi") ||
          this.saleOrderData?.salesOrderHeader?.salesOrderHeader?.addressSelectionD365
            ?.toLowerCase()
            .includes("tbd")
        )
      ) {
        this.addressObject = this.saleOrderData?.salesOrderHeader.address;
        if (this.shipTo) {
          this.shipTo.onDeliveryAddressChange("To Header Ship To");
          this.shipTo.patchFormInHeaderToShip(this.addressObject);
          this.shipTo.patchFormDeliveryAddress("To Header Ship To", "");
        }
      } else {
        const addressSelection =
          this.saleOrderData?.salesOrderHeader?.salesOrderHeader
            ?.addressSelectionD365;
        if (this.shipTo) {
          this.shipTo.patchFormDeliveryAddress(
            "To Header Ship To",
            addressSelection ?? ""
          );
        }
      }
      if (this.saleOrderData?.salesOrderHeader?.salesOrderHeader) {
        this.headerObject =
          this.saleOrderData.salesOrderHeader.salesOrderHeader;
        if (
          this.headerObject?.addressSelectionD365
            ?.toLowerCase()
            ?.includes("multi")
        ) {
          this.headerObject.deliveryNameD365 = "Multiple Drop Ships";
        } else if (
          this.headerObject?.addressSelectionD365
            ?.toLowerCase()
            ?.includes("tbd")
        ) {
          this.headerObject.deliveryNameD365 = "TBD";
        }

        if (this.shipTo) {
          this.shipTo.patchFormInHeaderToShipAddress(this.headerObject);
        }
      }
    }

    if (this.shippingProductsList.length === 0) {
      this.getAllShippingMethod();
    }

    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader
        ?.shippingMethodD365 &&
      !this.orderLineNumber
    ) {
      this.shippingMethod =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.shippingMethodD365;
      this.patchValueInShippingMethod(this.shippingMethod);

      if (this.shippingMethod) {
        this.onShippingMethodSelect(this.shippingMethod);
      }
    }

    if (this.saleOrderData?.salesOrderLine) {
      if (this.saleOrderData?.salesOrderLine.length > 0) {
        this.saleOrderLine = this.saleOrderData.salesOrderLine;

        if (this.orderLineNumber && this.orderLineNumber) {
          const saleOrderLineList = this.saleOrderData?.salesOrderLine.filter(
            x => x.salesOrderLine?.id === this.orderLineNumber
          );
          if (saleOrderLineList[0]?.salesOrderLine) {
            this.showHideButtons(
              saleOrderLineList[0].salesOrderLine.proposalIdD365
            );
            this.setModDates(saleOrderLineList[0]?.salesOrderLine);

            const number = saleOrderLineList[0]?.salesOrderLine?.lineNumberD365;
            this.orderItemLineNumber = number || 1;
            this.patchSalesClassficationValue(
              saleOrderLineList[0]?.salesOrderLine?.salesClassificationIdD365 ??
                0
            );

            this.selectedItemNumber =
              saleOrderLineList[0].salesOrderLine.itemNumberD365 ?? "";
          }
        } else {
          this.getLineNumber(this.saleOrderId);
        }
        this.patchValueByLineNumber(
          this.saleOrderData.salesOrderLine,
          this.sellCurrency
        );
      }
    }
    if (this.saleOrderLine.length > 0 && !this.orderLineNumber) {
      this.checkPOAssignment();
    }

    if (!this.isEdit) {
      this.poAssignment = "Default";
      this.patchValueInPoAssignment(this.poAssignment);
      this.saleOrderItemLine.patchValue({
        vendor: {
          vendorPoAssignment: "Default",
        },
      });
    }

    if (this.sellCurrencyStatus && this.lineDetail) {
      this.lineDetail.getSellCurrency(this.sellCurrency);
    }
    if (
      this.saleOrderData?.salesOrderHeader?.salesOrderHeader?.customerIdD365
    ) {
      this._orderLineItem
        .getPartyNumberByAccountNumber(
          this.saleOrderData.salesOrderHeader?.salesOrderHeader.customerIdD365
        )
        .subscribe(x => {
          this.patchValueInCustomerAddressField(
            this.saleOrderData?.salesOrderHeader?.salesOrderHeader
              ?.customerIdD365 ?? "",
            x.data ?? ""
          );
        });
    }

    if (this.saleOrderData?.salesOrderLine) {
      if (
        this.saleOrderData.salesOrderLine?.length > 0 &&
        this.saleOrderData.salesOrderLine[0]?.salesOrderLine
      ) {
        this.selectedLineStatusD365 =
          this.saleOrderData.salesOrderLine[0].salesOrderLine.lineStatusD365 ??
          "";
        this.orderedPurchaseQuantityD365 =
          this.saleOrderData.salesOrderLine[0].salesOrderLine
            .orderedPurchaseQuantityD365 ?? 0;
        this.deliverRemainderD365 =
          this.saleOrderData.salesOrderLine[0].salesOrderLine
            .deliverRemainderD365 ?? 0;
        this.checkShowHideFields();
      }
    }

    if (this.saleOrderData?.salesOrderHeader?.salesOrderHeader?.programId) {
      this.programId =
        this.saleOrderData.salesOrderHeader?.salesOrderHeader?.programId;
      //this.isSalesClassificationRequired();
    }

    this._loaderService.setLoaderState(false);
  }

  checkPOAssignment() {
    if (this.saleOrderLine.length > 0) {
      const vendorNumber = this.saleOrderItemLine.get([
        "vendor",
        "vendorDirectDelivery",
      ])?.value;
      let lineStatus = false;
      this.saleOrderLine.forEach(x => {
        if (x.salesOrderLine?.vendorDirectDeliveryD365 === vendorNumber) {
          lineStatus = true;
        }
      });

      if (lineStatus) {
        this.poAssignment = "Default";
        this.patchValueInPoAssignment(this.poAssignment);
      } else {
        if (this.isFromCdi) {
          this.poAssignment = "Existing";
          this.getVendorDetails(this.linesVendorNumber);
          // this.purchaseOrderId = this.poNumber;
        } else {
          this.poAssignment = "New";
          if (
            this.lineActionResult === "Copy Header" ||
            this.lineActionResult === "Copy Line" ||
            this.lineActionResult === "Add Setup" ||
            this.lineActionResult === "Misc"
          ) {
            this.poAssignment = "Default";
            this.patchValueInPoAssignment(this.poAssignment);
            this.saleOrderItemLine.patchValue({
              vendor: {
                vendorPoAssignment: "Default",
              },
            });
          }
        }

        if (!this.isEdit && this.poAssignment !== "Existing") {
          this.poAssignment = "Default";
          this.patchValueInPoAssignment(this.poAssignment);
          this.saleOrderItemLine.patchValue({
            vendor: {
              vendorPoAssignment: "Default",
            },
          });
        }

        this.patchValueInPoAssignment(this.poAssignment);
      }
    }
  }

  getVendorDetails(vendorLineNumber: string) {
    this.getSourcingVendor(vendorLineNumber);
  }

  getCustomerFinancialDimension(customerAccountNumber: string) {
    this._addNewClientService
      .getCustomerFinancialDimension(customerAccountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.customerFinancialDimension = res.data;
        },
      });
  }

  patchValueInPoAssignment(poAssigment: string) {
    this.saleOrderItemLine.patchValue({
      vendor: {
        vendorPoAssignment: poAssigment,
      },
    });
  }

  openSideBarForPoAssignment(event: string) {
    if (event === "Existing") {
      this.displaySidebar = true;
    } else {
      this.displaySidebar = false;
    }
  }

  patchValueInCustomerAddressField(accountNumber: string, partyNumber: string) {
    this.customerBillToObject = {
      partyNumber: partyNumber,
      accountNumber: accountNumber,
    };
    this._customerAccountService
      .getCustomerAddressByPartyNumber(partyNumber, accountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          const primaryAddress = res.data.filter(
            (x: Address) =>
              x.isPrimaryD365?.toLowerCase() === "yes" ||
              x.isPrimaryD365?.toLowerCase() === "1" ||
              x.rolesD365?.toLowerCase() === "delivery"
          );
          this.customerLists = primaryAddress;
          const currentClientObject = this.customerLists?.filter(
            x =>
              x?.locationNumberD365 ===
                this.currentSaleOrderLine?.locationNumberD365 &&
              x?.addressD365 === this.currentSaleOrderLine?.addressD365
          );
          if (currentClientObject?.length > 0) {
            this.shipTo.patchValueInCustomerShipToName(
              currentClientObject[0]?.descriptionD365 ?? ""
            );
          }
        },
      });
  }

  patchValueInFirm(firm: boolean) {
    this.saleOrderItemLine.patchValue({
      detail: {
        firm: firm,
      },
    });
  }

  onShippingMethodSelect(shippingMethodCode: string) {
    if (!this.orderLineNumber) {
      const index = this.shippingProductsList.findIndex(
        x => x.modeCodeD365 == shippingMethodCode
      );

      if (index > -1) {
        const selectedShippingMethod = this.shippingProductsList[index];
        if (
          selectedShippingMethod.useFreightAccountD365?.toLowerCase() == "yes"
        ) {
          this.saleOrderItemLine.patchValue({
            detail: {
              accountNumberD365: selectedShippingMethod.jniFreightAcctNumD365,
            },
          });
        } else {
          this._orderLineItemDetailService
            .getClientFreightAccountNumber(this.customerId)
            .subscribe({
              next: res => {
                if (res.data) {
                  this.saleOrderItemLine.patchValue({
                    detail: {
                      accountNumberD365: res.data,
                    },
                  });
                }
              },
            });
        }
      }
    }
  }

  patchValueInShippingMethod(shippingMethod: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        shippingMethod: shippingMethod,
      },
    });
  }

  patchValueInShipDate(shipdate: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        shipDate: shipdate,
      },
    });
  }

  patchValueInHandDate(inhandate: string) {
    this.saleOrderItemLine.patchValue({
      detail: {
        inHandDate: inhandate,
      },
    });
  }

  getLineNumber(saleOrderId: string) {
    return this._orderLineItem.getSaleOrderLineNumber(saleOrderId).subscribe({
      next: x => {
        this.orderItemLineNumber = x.data || 1;
      },
    });
  }

  clearSaleOrderLineForm() {
    this.saleOrderItemLine.patchValue({
      vendor: {
        duplicateVendorDirectDelivery: "",
        vendorDirectDelivery: "",
        // vendorPoAssignment: '',
        vendorName: "",
        vendorAddress: "",
        vendorPhone: "",
        vendorEmail: "",
        vendorFax: "",
      },
      // shipTo: {
      //   deliveryAddress: '',
      //   attention: '',
      //   countryRegionIdD365: '',
      //   zipCodeD365: '',
      //   cityD365: '',
      //   stateD365: '',
      //   addressD365: '',
      //   streetD365: '',
      //   shipToEmail: '',
      //   addressBook: '',
      //   buildingComplimentD365: '',
      //   apartment_RUD365: '',
      //   postBoxD365: '',
      //   countyD365: '',
      //   streetNumberD365: '',
      //   districtNameD365: '',
      //   shipToEmailAddress: '',
      //   addressSelectionName: '',
      //   locationD365: ''
      // },
      detail: {
        firm: false,
      },
      lines: {
        itemNumberD365: "",
        externalItemNumberD365: "",
        orderedPurchaseQuantityD365: "",
        isnetInForeignCurrencyStatus: "",
        purchasePriceD365: 0,
        netTotalD365: 0,
        netConversion: "",
        netConversionTotal: "",
        sell: 0,
        sellTotal: "",
        sellConversion: "",
        sellConversionTotal: "",
        commission: 0,
        grossProfit: 0,
        lineDescriptionD365: "",
        factoryNotes: "",
        currencyCodeD365: "",
        rates: 0,
        salesClassificationD365: "",
        size: "",
        color: "",
        config: "",
        style: "",
        categoryName: "",
      },
    });
  }

  clearLineData() {
    this.saleOrderItemLine.patchValue({
      lines: {
        itemNumberD365: "",
        externalItemNumberD365: "",
        orderedPurchaseQuantityD365: "",
        isnetInForeignCurrencyStatus: "",
        purchasePriceD365: 0,
        netTotalD365: 0,
        netConversion: "",
        netConversionTotal: "",
        sell: 0,
        sellTotal: "",
        sellConversion: "",
        sellConversionTotal: "",
        commission: 0,
        grossProfit: 0,
        lineDescriptionD365: "",
        factoryNotes: "",
        currencyCodeD365: "",
        rates: 0,
        salesClassificationD365: "",
        size: "",
        color: "",
        config: "",
        style: "",
        categoryName: "",
      },
    });
  }

  async next(lineAction: string) {
    this.submitButtonlogValidationErrors();
    // const isValidSalesClassification = this.saleOrderItemLine.get(['lines','salesClassificationD365'])?.valid;
    // if(isValidSalesClassification){
    //   this.formErrors.salesClassificationD365 = this.validationMessages?.salesClassificationD365.required;
    // }
    this.lineActionResult = lineAction;
    const saleOrderLineFormObject =
      this.saleOrderItemLine.getRawValue() as SaleOrderItemLineFormDto;
    if (lineAction === "Add Setup") {
      this.saleOrderItemLine.get(["lines", "isTrackable"])?.setValue(false);
      this.saleOrderItemLine.get(["lines", "isTrackable"])?.disable();
      this.addSetupIsnetInForeignCurrencyValue =
        saleOrderLineFormObject.lines?.isnetInForeignCurrencyStatus ?? false;
      this.addSetupNetCurrencyValue =
        saleOrderLineFormObject.lines?.currencyCodeD365 ?? "";

      if (
        saleOrderLineFormObject?.lines?.salesClassificationD365 &&
        typeof saleOrderLineFormObject?.lines?.salesClassificationD365 ===
          "number"
      ) {
        this.setupLineCopySalesClassification =
          saleOrderLineFormObject.lines.salesClassificationD365;
      }
    }

    if (lineAction === "Misc") {
      this.addSetupIsnetInForeignCurrencyValue =
        saleOrderLineFormObject.lines?.isnetInForeignCurrencyStatus ?? false;
      this.addSetupNetCurrencyValue =
        saleOrderLineFormObject.lines?.currencyCodeD365 ?? "";
    }

    if (
      (lineAction === "Copy Header" ||
        lineAction === "Copy Line" ||
        lineAction === "Add Setup" ||
        lineAction === "Misc") &&
      this.orderType === "inventory-sales"
    ) {
      saleOrderLineFormObject.vendor.vendorPoAssignment = "Default";
      this.poAssignment = "Default";
      this.patchValueInPoAssignment(this.poAssignment);
      this.saleOrderItemLine.patchValue({
        vendor: {
          vendorPoAssignment: "Default",
        },
      });
    }
    if (saleOrderLineFormObject.lines) {
      saleOrderLineFormObject.lines.portalKey = uuidv4();
    }
    const linesArray = [] as SaleOrderLinesDto[];
    linesArray.push(saleOrderLineFormObject.lines as SaleOrderLinesDto);
    linesArray.forEach(x => {
      if (x?.currencyCodeD365 === "") {
        x.isnetInForeignCurrencyStatus = false;
      } else if (x?.currencyCodeD365 && x.currencyCodeD365 !== "USD") {
        x.isnetInForeignCurrencyStatus = true;
      } else {
        x.isnetInForeignCurrencyStatus = false;
      }
    });

    if (
      saleOrderLineFormObject.detail &&
      !saleOrderLineFormObject.detail.shipDate
    ) {
      // this._loaderService.setLoaderState(false);
      // this._toastr.error('Error', 'Ship date is required');
      // return;
      saleOrderLineFormObject.detail.shipDate = null;
    }

    if (!saleOrderLineFormObject.detail?.inHandDate) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Inhand date is required");
      return;
    }

    if (
      saleOrderLineFormObject.detail?.shipDate &&
      saleOrderLineFormObject.detail?.inHandDate
    ) {
      if (
        new Date(saleOrderLineFormObject.detail?.inHandDate) <
        new Date(saleOrderLineFormObject.detail?.shipDate)
      ) {
        this._loaderService.setLoaderState(false);
        this._toastr.error(
          "Error",
          "Inhands date should be greater and equal to ship date"
        );
        return;
      }
    }

    if (
      saleOrderLineFormObject.shipTo &&
      saleOrderLineFormObject.shipTo.addressLocationNumber === 0
    ) {
      saleOrderLineFormObject.shipTo.addressLocationNumber = 0;
    }

    if (
      saleOrderLineFormObject?.shipTo &&
      !saleOrderLineFormObject.shipTo?.addressSelectionName
    ) {
      this._toastr.error("Error", "Ship To Delivery is required");
      this._loaderService.setLoaderState(false);
      return;
    }

    const addressD365 = this.headerObject?.addressSelectionD365 ?? "";
    if (
      addressD365 &&
      (saleOrderLineFormObject.shipTo?.addressSelectionName
        ?.toLowerCase()
        ?.includes("header") ||
        saleOrderLineFormObject.shipTo?.addressSelectionName
          ?.toLowerCase()
          ?.includes("header")) &&
      (addressD365?.toLowerCase()?.includes("multi") ||
        addressD365?.toLowerCase()?.includes("drop") ||
        addressD365?.toLowerCase()?.includes("tbd"))
    ) {
      saleOrderLineFormObject.shipTo.addressD365 = "";
    }

    if (
      !(
        (saleOrderLineFormObject.shipTo?.addressSelectionName ==
          "To Header Ship To" ||
          saleOrderLineFormObject.shipTo?.addressSelectionName ==
            "To header ship to" ||
          saleOrderLineFormObject.shipTo?.addressSelectionName ==
            "To header ship-to") &&
        (this.headerObject.addressSelectionD365 == "TBD" ||
          this.headerObject.addressSelectionD365 == "Multiple ship-to" ||
          this.headerObject.addressSelectionD365
            ?.toLowerCase()
            .includes("drop") ||
          this.headerObject.addressSelectionD365 == "Multi Ship To")
      )
    ) {
      if (!saleOrderLineFormObject.shipTo?.countryRegionIdD365) {
        this._toastr.error("Error", "Address is required");
        this._loaderService.setLoaderState(false);
        return;
      }
      if (
        !saleOrderLineFormObject.shipTo.zipCodeD365 &&
        saleOrderLineFormObject.shipTo.countryRegionIdD365 === "USA"
      ) {
        this._toastr.error("Error", "Address is required");
        this._loaderService.setLoaderState(false);
        return;
      }
    }

    if (
      saleOrderLineFormObject.lines &&
      "commission" in saleOrderLineFormObject.lines &&
      saleOrderLineFormObject.lines?.commission === 0
    ) {
      saleOrderLineFormObject.lines.commission = 0;
    }

    if (
      saleOrderLineFormObject.lines &&
      "grossProfit" in saleOrderLineFormObject.lines &&
      saleOrderLineFormObject.lines?.grossProfit === 0
    ) {
      saleOrderLineFormObject.lines.grossProfit = 0;
    }
    if (saleOrderLineFormObject.lines?.orderedPurchaseQuantityD365 === 0) {
      this._toastr.error("Error", "Quantity required");
      this._loaderService.setLoaderState(false);
      return;
    }

    if (
      saleOrderLineFormObject.shipTo.attention &&
      saleOrderLineFormObject.lines
    ) {
      saleOrderLineFormObject.lines.attentionInformationD365 =
        saleOrderLineFormObject.shipTo.attention;
    }
    if (
      saleOrderLineFormObject.shipTo.locationD365 &&
      saleOrderLineFormObject.lines
    ) {
      saleOrderLineFormObject.lines.locationD365 =
        saleOrderLineFormObject.shipTo.locationD365;
    }

    if (!this.saleOrderItemLine.get(["shipTo", "validateAddress"])?.valid) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Please validate the address");
      return;
    }

    if (
      !this.purchaseOrderId &&
      saleOrderLineFormObject.vendor.vendorPoAssignment === "Existing"
    ) {
      const hasPO = await firstValueFrom(
        this.getPurchaseOrderId(this.saleOrderId, this.directDeliveryVendor)
      );
      if (hasPO) {
        this.displaySidebar = true;
        return;
      }
    }

    if (
      this.isMelissaValidationIgnore ||
      this.isCopiedLineMelissaValidationIgnore
    ) {
      this.saleOrderItemLine.get(["shipTo", "streetD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "streetD365"])
        ?.updateValueAndValidity();
      this.saleOrderItemLine.get(["shipTo", "addressD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "addressD365"])
        ?.updateValueAndValidity();
      this.saleOrderItemLine.get(["shipTo", "zipCodeD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "zipCodeD365"])
        ?.updateValueAndValidity();
    }

    if (!this.saleOrderItemLine.valid) {
      this._loaderService.setLoaderState(false);
      return;
    }

    const currency = this.saleOrderItemLine.get([
      "lines",
      "currencyCodeD365",
    ])?.value;
    if (currency === "USD") {
      this.saleOrderItemLine.patchValue({
        lines: {
          netConversion: "",
          netConversionTotal: "",
        },
      });
    }

    if (
      saleOrderLineFormObject.lines?.salesClassificationD365 &&
      saleOrderLineFormObject.lines.salesClassificationD365 !== ""
    ) {
      const salesClassificationList = [];
      salesClassificationList.push({
        displayValue:
          saleOrderLineFormObject.lines.salesClassificationD365.toString() ??
          "",
        displayName: this.salesClassificationList.find(
          x =>
            x.value ===
            (saleOrderLineFormObject.lines?.salesClassificationD365 as string)
        )?.name,
      });
      linesArray[0].salesClassificationD365 = salesClassificationList;
    }

    this._loaderService.setLoaderState(true);
    if (
      this.lineId &&
      !["Add Line", "Copy Header", "Copy Line", "Add Setup", "Misc"].includes(
        lineAction
      )
    ) {
      const saleOrderLineRequest: UpdateSalesOrderLineDto = {
        id: this.lineId,
        vendor: saleOrderLineFormObject.vendor,
        shipTo: saleOrderLineFormObject.shipTo,
        Detail: saleOrderLineFormObject.detail,
        lines: linesArray[0] as unknown as SaleOrderLinesDto,
        purchaseOrderId: this.purchaseOrderId,
        salesOrderId: this.saleOrderId,
      };

      if (saleOrderLineRequest.shipTo) {
        const formValue = this.saleOrderItemLine.getRawValue();
        const value = formValue.shipTo.shipToVendorAccountNameD365 || "";
        saleOrderLineRequest.shipTo.deliveryAddressD365 =
          this.getDeliveryAddress(
            saleOrderLineRequest.shipTo.addressSelectionName ?? "",
            saleOrderLineRequest.shipTo.deliveryNameD365 ?? "",
            value || ""
          );

        saleOrderLineRequest.shipTo.shipToD365Address =
          formValue.shipTo.addressD365 || "";
      }

      this.checkCurrencyMismatch(
        saleOrderLineRequest,
        CheckCurrencyMismatchCallingMethod.nextMethodUpdateSaleOrderLine,
        ""
      );
    } else {
      if (
        saleOrderLineFormObject.shipTo?.addressSelectionName
          ?.toLowerCase()
          .includes("one")
      ) {
        saleOrderLineFormObject.shipTo.addressLocationNumber = 0;
      }
      const saleOrderLineRequest: SalesOrderLinesRequestDto = {
        saleOrderId: this.saleOrderId,
        vendor: saleOrderLineFormObject.vendor,
        shipTo: saleOrderLineFormObject.shipTo,
        Detail: saleOrderLineFormObject.detail,
        lines: linesArray as unknown as SalesOrderLine[],
        purchaseOrderId: this.purchaseOrderId,
        saveToPortal: false,
      };

      if (saleOrderLineRequest.shipTo) {
        const formValue = this.saleOrderItemLine.getRawValue();
        const value = formValue.shipTo.shipToVendorAccountNameD365 || "";
        saleOrderLineRequest.shipTo.deliveryAddressD365 =
          this.getDeliveryAddress(
            saleOrderLineRequest.shipTo.addressSelectionName ?? "",
            saleOrderLineRequest.shipTo.deliveryNameD365 ?? "",
            value || ""
          );

        saleOrderLineRequest.shipTo.shipToD365Address =
          formValue.shipTo.addressD365 || "";
      }

      this.checkCurrencyMismatch(
        saleOrderLineRequest,
        CheckCurrencyMismatchCallingMethod.nextMethodCreateSaleOrderLine,
        lineAction
      );
    }
  }

  unTouchForm() {
    this.saleOrderItemLine.markAsUntouched();
    this.saleOrderItemLine.markAsPristine();
    this.formErrors = {
      duplicateVendorDirectDelivery: "",
      itemNumberD365: "",
      orderedPurchaseQuantityD365: "",
      vendorPoAssignment: "",
      shipDate: "",
      inHandDate: "",
      addressSelectionName: "",
      salesClassificationD365: "",
      size: "",
      color: "",
      config: "",
      style: "",
      categoryName: "",
    };
  }

  patchValueInSetup() {
    this.saleOrderItemLine.patchValue({
      lines: {
        itemNumberD365: "Set Up",
        externalItemNumberD365: "SET UP",
        orderedPurchaseQuantityD365: 1,
        lineDescriptionD365: "SET UP",
        factoryNotes: "SET UP",
        currencyCodeD365: this.addSetupNetCurrencyValue,
        isnetInForeignCurrencyStatus: this.addSetupIsnetInForeignCurrencyValue,
        salesClassificationD365: this.setupLineCopySalesClassification,
      },
    });
    this.currencyFromVendorEvent(this.addSetupNetCurrencyValue);
  }

  patchValueInMisc() {
    this.saleOrderItemLine.patchValue({
      lines: {
        itemNumberD365: "Misc",
        externalItemNumberD365: "Misc",
        orderedPurchaseQuantityD365: 1,
        lineDescriptionD365: "Additional fee related to sell of inventory",
        factoryNotes: "",
        currencyCodeD365: this.addSetupNetCurrencyValue,
        isnetInForeignCurrencyStatus: this.addSetupIsnetInForeignCurrencyValue,
      },
    });
    this.selectedItemNumber = "Misc";
    this.currencyFromVendorEvent(this.addSetupNetCurrencyValue);
  }

  submitButtonlogValidationErrors(
    group: FormGroup = this.saleOrderItemLine
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.submitButtonlogValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (abstractControl && !abstractControl.valid) {
          let messages = this.validationMessages[key];
          if (
            this.orderType === "inventory-sales" &&
            key === "itemNumberD365"
          ) {
            messages = this.validationMessages["inventoryItemNumberD365"];
          }
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  async onSubmit() {
    this.submitButtonlogValidationErrors();
    this.onSubmitDone = true;

    const saleOrderLineFormObject =
      this.saleOrderItemLine.getRawValue() as SaleOrderItemLineFormDto;
    if (saleOrderLineFormObject.lines) {
      saleOrderLineFormObject.lines.portalKey = uuidv4();
    }
    const linesArray = [];
    linesArray.push(saleOrderLineFormObject.lines);
    linesArray.forEach(x => {
      if (x?.currencyCodeD365 === "") {
        x.isnetInForeignCurrencyStatus = false;
      }

      if (x?.currencyCodeD365 && x.currencyCodeD365 !== "USD") {
        x.isnetInForeignCurrencyStatus = true;
      }
      if (x?.currencyCodeD365 && x.currencyCodeD365 === "USD") {
        x.isnetInForeignCurrencyStatus = false;
      }
      if (x?.currencyCodeD365 === "") {
        x.isnetInForeignCurrencyStatus = false;
      }
    });

    if (
      saleOrderLineFormObject.detail &&
      !saleOrderLineFormObject.detail.shipDate
    ) {
      // this._loaderService.setLoaderState(false);
      // this._toastr.error('Error', 'Ship date is required');
      // return;
      saleOrderLineFormObject.detail.shipDate = null;
    }

    // if (
    //   (!saleOrderLineFormObject['vendor']?.vendorName ||
    //     !saleOrderLineFormObject['vendor']?.vendorDirectDelivery) &&
    //   saleOrderLineFormObject['lines']?.purchasePriceD365
    // ) {
    //   this._loaderService.setLoaderState(false);
    //   this._toastr.error('Error', 'Vendor is required');
    //   return;
    // }

    if (!saleOrderLineFormObject.detail?.inHandDate) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Inhand date is required");
      return;
    }

    if (
      saleOrderLineFormObject.detail?.shipDate &&
      saleOrderLineFormObject.detail?.inHandDate
    ) {
      if (
        new Date(saleOrderLineFormObject.detail?.inHandDate) <
        new Date(saleOrderLineFormObject.detail?.shipDate)
      ) {
        this._loaderService.setLoaderState(false);
        this._toastr.error(
          "Error",
          "Inhands date should be greater and equal to ship date"
        );
        return;
      }
    }

    if (
      saleOrderLineFormObject.shipTo &&
      saleOrderLineFormObject.shipTo.addressLocationNumber === 0
    ) {
      saleOrderLineFormObject.shipTo.addressLocationNumber = 0;
    }

    if (
      saleOrderLineFormObject?.shipTo &&
      !saleOrderLineFormObject.shipTo?.addressSelectionName
    ) {
      this._toastr.error("Error", "Ship To Delivery is required");
      this._loaderService.setLoaderState(false);
      return;
    }

    const addressD365 = this.headerObject?.addressSelectionD365 ?? "";
    if (
      addressD365 &&
      (saleOrderLineFormObject.shipTo?.addressSelectionName
        ?.toLowerCase()
        ?.includes("header") ||
        saleOrderLineFormObject.shipTo?.addressSelectionName
          ?.toLowerCase()
          ?.includes("header")) &&
      (addressD365?.toLowerCase()?.includes("multi") ||
        addressD365?.toLowerCase()?.toLowerCase().includes("drop") ||
        addressD365?.toLowerCase()?.includes("tbd"))
    ) {
      saleOrderLineFormObject.shipTo.addressD365 = "";
    }

    if (
      !(
        (saleOrderLineFormObject.shipTo?.addressSelectionName ==
          "To Header Ship To" ||
          saleOrderLineFormObject.shipTo?.addressSelectionName ==
            "To header ship to" ||
          saleOrderLineFormObject.shipTo?.addressSelectionName ==
            "To header ship-to") &&
        (this.headerObject.addressSelectionD365 == "TBD" ||
          this.headerObject.addressSelectionD365 == "Multiple ship-to" ||
          this.headerObject.addressSelectionD365
            ?.toLowerCase()
            .includes("drop") ||
          this.headerObject.addressSelectionD365 == "Multi Ship To")
      )
    ) {
      if (!saleOrderLineFormObject.shipTo?.countryRegionIdD365) {
        this._toastr.error("Error", "Address is required");
        this._loaderService.setLoaderState(false);
        return;
      }

      if (
        !saleOrderLineFormObject.shipTo.zipCodeD365 &&
        saleOrderLineFormObject.shipTo.countryRegionIdD365 === "USA"
      ) {
        this._toastr.error("Error", "Address is required");
        this._loaderService.setLoaderState(false);
        return;
      }
    }

    if (
      saleOrderLineFormObject.lines &&
      "commission" in saleOrderLineFormObject.lines &&
      saleOrderLineFormObject.lines?.commission === 0
    ) {
      saleOrderLineFormObject.lines.commission = 0;
    }

    if (
      saleOrderLineFormObject.lines &&
      "grossProfit" in saleOrderLineFormObject.lines &&
      saleOrderLineFormObject.lines?.grossProfit === 0
    ) {
      saleOrderLineFormObject.lines.grossProfit = 0;
    }

    if (saleOrderLineFormObject.lines?.orderedPurchaseQuantityD365 === 0) {
      this._toastr.error("Error", "Quantity required");
      this._loaderService.setLoaderState(false);
      return;
    }

    if (
      saleOrderLineFormObject.shipTo.attention &&
      saleOrderLineFormObject.lines
    ) {
      saleOrderLineFormObject.lines.attentionInformationD365 =
        saleOrderLineFormObject.shipTo.attention;
    }
    if (
      saleOrderLineFormObject.shipTo.locationD365 &&
      saleOrderLineFormObject.lines
    ) {
      saleOrderLineFormObject.lines.locationD365 =
        saleOrderLineFormObject.shipTo.locationD365;
    }

    if (!this.saleOrderItemLine.get(["shipTo", "validateAddress"])?.valid) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Please validate the address");
      return;
    }

    if (
      !this.purchaseOrderId &&
      saleOrderLineFormObject.vendor.vendorPoAssignment === "Existing"
    ) {
      const hasPO = await firstValueFrom(
        this.getPurchaseOrderId(this.saleOrderId, this.directDeliveryVendor)
      );
      if (hasPO) {
        this.displaySidebar = true;
        return;
      }
    }

    if (
      this.isMelissaValidationIgnore ||
      this.isCopiedLineMelissaValidationIgnore
    ) {
      this.saleOrderItemLine.get(["shipTo", "streetD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "streetD365"])
        ?.updateValueAndValidity();
      this.saleOrderItemLine.get(["shipTo", "addressD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "addressD365"])
        ?.updateValueAndValidity();
      this.saleOrderItemLine.get(["shipTo", "zipCodeD365"])?.clearValidators();
      this.saleOrderItemLine
        .get(["shipTo", "zipCodeD365"])
        ?.updateValueAndValidity();
    }

    if (!this.saleOrderItemLine.valid) {
      this._loaderService.setLoaderState(false);
      return;
    }
    const currency = this.saleOrderItemLine.get([
      "lines",
      "currencyCodeD365",
    ])?.value;
    if (currency === "USD") {
      this.saleOrderItemLine.patchValue({
        lines: {
          netConversion: "",
          netConversionTotal: "",
        },
      });
    }

    if (
      saleOrderLineFormObject.lines?.salesClassificationD365 &&
      saleOrderLineFormObject.lines.salesClassificationD365 !== ""
    ) {
      if (linesArray?.[0]) {
        const salesClassificationList = [
          {
            displayValue:
              saleOrderLineFormObject.lines.salesClassificationD365.toString() ??
              "",
            displayName:
              this.salesClassificationList.find(
                x =>
                  x.value ===
                  saleOrderLineFormObject.lines?.salesClassificationD365
              )?.name ?? "",
          },
        ];
        linesArray[0].salesClassificationD365 = salesClassificationList;
      }
    }

    this._loaderService.setLoaderState(true);

    if (
      this.isMelissaValidationIgnore ||
      this.isCopiedLineMelissaValidationIgnore
    ) {
      this.onSaleOrderNotesSave();
      if (saleOrderLineFormObject.shipTo) {
        saleOrderLineFormObject.shipTo.isShipToAddressValidationIgnored =
          this.isMelissaValidationIgnore ||
          this.isCopiedLineMelissaValidationIgnore;
      }
    }

    if (this.lineId) {
      const saleOrderLineRequest: UpdateSalesOrderLineDto = {
        id: this.lineId,
        vendor: saleOrderLineFormObject.vendor,
        shipTo: saleOrderLineFormObject.shipTo,
        Detail: saleOrderLineFormObject.detail,
        lines: linesArray[0] as unknown as SaleOrderLinesDto,
        purchaseOrderId: this.purchaseOrderId,
        salesOrderId: this.saleOrderId,
      };

      if (saleOrderLineRequest.shipTo) {
        const formValue = this.saleOrderItemLine.getRawValue();
        const value = formValue.shipTo.shipToVendorAccountNameD365 || "";
        saleOrderLineRequest.shipTo.deliveryAddressD365 =
          this.getDeliveryAddress(
            saleOrderLineRequest.shipTo.addressSelectionName ?? "",
            saleOrderLineRequest.shipTo.deliveryNameD365 ?? "",
            value || ""
          );

        saleOrderLineRequest.shipTo.shipToD365Address =
          formValue.shipTo.addressD365 || "";
      }

      this.checkCurrencyMismatch(
        saleOrderLineRequest,
        CheckCurrencyMismatchCallingMethod.onSubmitMethodUpdateSaleOrderLine,
        ""
      );
    } else {
      if (
        saleOrderLineFormObject.shipTo?.addressSelectionName
          ?.toLowerCase()
          .includes("one")
      ) {
        saleOrderLineFormObject.shipTo.addressLocationNumber = 0;
      }

      const saleOrderLineRequest: SalesOrderLinesRequestDto = {
        saleOrderId: this.saleOrderId,
        vendor: saleOrderLineFormObject.vendor,
        shipTo: saleOrderLineFormObject.shipTo,
        Detail: saleOrderLineFormObject.detail,
        lines: linesArray as unknown as SalesOrderLine[],
        purchaseOrderId: this.purchaseOrderId,
        saveToPortal: this.saveToPortal,
      };

      if (saleOrderLineRequest.shipTo) {
        const formValue = this.saleOrderItemLine.getRawValue();
        const value = formValue.shipTo.shipToVendorAccountNameD365 || "";
        saleOrderLineRequest.shipTo.deliveryAddressD365 =
          this.getDeliveryAddress(
            saleOrderLineRequest.shipTo.addressSelectionName ?? "",
            saleOrderLineRequest.shipTo.deliveryNameD365 ?? "",
            value || ""
          );

        saleOrderLineRequest.shipTo.shipToD365Address =
          formValue.shipTo.addressD365 ?? "";
      }
      this.checkCurrencyMismatch(
        saleOrderLineRequest,
        CheckCurrencyMismatchCallingMethod.onSubmitMethodCreateSaleOrderLine,
        ""
      );
    }
  }

  showPositionDialogDescription(
    position:
      | "center"
      | "top"
      | "bottom"
      | "left"
      | "right"
      | "topleft"
      | "topright"
      | "bottomleft"
      | "bottomright"
  ) {
    this.position = position;
    this.displayPositionDescription = true;
    this.stockPhraseDescription.flag = "description";
    this.stockPhraseDescription.selectedStock = [];
    this.stockPhraseDescription.getStockPhraseData();
  }

  showPositionDialogFactory(
    position:
      | "center"
      | "top"
      | "bottom"
      | "left"
      | "right"
      | "topleft"
      | "topright"
      | "bottomleft"
      | "bottomright"
  ) {
    this.position = position;
    this.displayPositionFactory = true;
    this.stockPhraseFactory.flag = "factory";
    this.stockPhraseFactory.selectedStock = [];
    this.stockPhraseFactory.getStockPhraseData();
  }

  getDeliveryAddress(
    addressSelectionName: string,
    deliveryNameD365: string,
    shipToVendorAccountNameD365: string
  ) {
    if (addressSelectionName.toLowerCase().includes("one")) {
      return deliveryNameD365;
    } else if (addressSelectionName.toLowerCase().includes("bill")) {
      return deliveryNameD365;
    } else if (addressSelectionName.toLowerCase().includes("vendor")) {
      return shipToVendorAccountNameD365;
    } else if (addressSelectionName.toLowerCase().includes("address")) {
      return deliveryNameD365;
    } else if (addressSelectionName.toLowerCase().includes("branding")) {
      return deliveryNameD365;
    }
    return "";
  }

  closePositionDialogDescription() {
    this.displayPositionDescription = false;
  }

  closePositionDialogFactory() {
    this.displayPositionFactory = false;
  }

  getDescription(event: StockPhrase[]) {
    let description = "";
    event.forEach(element => {
      if (element.description) {
        if (description == "") {
          description += element.description;
        } else {
          description += "\n" + element.description;
        }
      }
    });

    let orderLineDescription = "";

    if (description) {
      orderLineDescription = `${
        this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.value ||
        ""
      }\n${description}`;
    } else {
      orderLineDescription = `${
        this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.value ||
        ""
      }`;
    }

    this.saleOrderItemLine.patchValue({
      lines: {
        lineDescriptionD365: orderLineDescription,
      },
    });
    this.closePositionDialogDescription();
  }

  getFactory(event: StockPhrase[]) {
    let fact = "";
    event.forEach(element => {
      if (element.notes) {
        if (fact == "") {
          fact += element.notes;
        } else {
          fact += "\n" + element.notes;
        }
      }
    });

    let orderLineFactoryNotes = "";

    if (fact) {
      orderLineFactoryNotes = `${
        this.saleOrderItemLine.get(["lines", "factoryNotes"])?.value || ""
      }\n${fact}`;
    } else {
      orderLineFactoryNotes = `${
        this.saleOrderItemLine.get(["lines", "factoryNotes"])?.value || ""
      }`;
    }

    this.saleOrderItemLine.patchValue({
      lines: {
        factoryNotes: orderLineFactoryNotes,
      },
    });
    this.closePositionDialogFactory();
  }

  addPurchaseOrderObject(event: string) {
    this.purchaseOrderId = event;
    this.displaySidebar = false;
    this.onSubmit();
  }

  // getLoaderStatus(status: boolean) {
  //   //this.showSmallLoader = status;
  // }

  collapseStatus = false;
  expandStatus = true;
  collapseHeading = "Collapse all";

  getStateOfCollapseAll() {
    const status = localStorage.getItem("sale-order-line-collapse");
    if (status) {
      if (status === "true") {
        this.collapseStatus = true;
        this.expandStatus = false;
        this.collapseHeading = "Expand all";
      } else {
        this.collapseStatus = false;
        this.expandStatus = true;
        this.collapseHeading = "Collapse all";
      }
    }
  }

  onToggle() {
    if (this.expandStatus) {
      this.collapseStatus = true;
      this.expandStatus = false;
      this.setStateOfExpandAll();
      this.collapseHeading = "Expand all";
    } else {
      this.collapseStatus = false;
      this.expandStatus = true;
      this.setStateOfCollapseAll();
      this.collapseHeading = "Collapse all";
    }
  }

  setStateOfExpandAll() {
    localStorage.setItem("sale-order-line-collapse", "true");
  }

  setStateOfCollapseAll() {
    localStorage.setItem("sale-order-line-collapse", "false");
  }

  ngOnDestroy(): void {
    this.scrollHiddenStatus.emit();
  }

  getUserProfile() {
    this._loginService
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.validateLoginObject = res.data;
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: async res => {
        if (res?.data) {
          this.userObject = res.data;
          this.getSalesOrderCommissionBc(res.data.personnelNumber);
        }
      },
    });
  }

  getSalesOrderCommissionBc(userPersonnelNumber: string) {
    this._orderSummaryService
      .getSalesOrderCommissionBc(this.saleOrderId)
      .subscribe({
        next: async res => {
          if (res?.data) {
            const ifFind = res.data.findIndex(
              x => x.employeeIDD365 == userPersonnelNumber
            );

            if (ifFind != -1 || this.userObject.userRoleId === UserRole.Admin) {
              this.showCommissionsFields = true;
            } else {
              this.showCommissionsFields = false;
            }
          } else {
            this.showCommissionsFields = false;
          }
        },
      });
  }

  openViewPoNumberPdfScreen(purchaseOrderId: string, salesOrderIdD365: string) {
    const url = `/orders/po-email/${salesOrderIdD365}/${purchaseOrderId}`;
    window.open(url, "_blank");
  }

  setModDates(salesOrderLine: SalesOrderLine | null) {
    if (salesOrderLine) {
      this.createdDate = salesOrderLine.createdDate ?? "";
      const createdByList = salesOrderLine.createdBy?.split("@") ?? [];
      if (createdByList && createdByList.length > 1) {
        this.createdBy = createdByList[0] ?? "";
      }
      const modifiedByList = salesOrderLine.lastModifiedBy?.split("@") ?? [];
      if (modifiedByList && modifiedByList.length > 1) {
        this.modifiedBy = modifiedByList[0] ?? "";
      }
      this.modifiedDate = salesOrderLine.lastModifiedDate ?? "";
    }
  }

  closeModalForSaleOrder() {
    this.orderDisplayModal = false;
  }

  redirectToCopiedFrom() {
    if (this.saleOrderLineHistory?.lineCopiedFrom) {
      if (
        this.saleOrderLineHistory?.lineCopiedFrom === EnumCopiedFrom.IsFromOrder
      ) {
        const url = `/orders/order-summary/${this.saleOrderLineHistory.lineCopiedFromHeaderId}`;
        window.open(url, "_blank");
      } else if (
        this.saleOrderLineHistory?.lineCopiedFrom === EnumCopiedFrom.IsFromQuote
      ) {
        const url = `/proposal/quote-summary/${this.saleOrderLineHistory.lineCopiedFromHeaderId}`;
        window.open(url, "_blank");
      }
    }
  }

  setCopiedFromInfo() {
    let copiedInfo = "";

    if (this.saleOrderLineHistory?.lineCopiedFromPurchaseOrderD365) {
      copiedInfo =
        " PO " +
        this.saleOrderLineHistory?.lineCopiedFromPurchaseOrderD365 +
        "   Line " +
        this.saleOrderLineHistory?.lineCopiedFromLineNumberD365 +
        "   ";
    } else if (this.saleOrderLineHistory?.lineCopiedFromHeaderId) {
      if (
        this.saleOrderLineHistory.lineCopiedFrom === EnumCopiedFrom.IsFromOrder
      ) {
        copiedInfo =
          " Ord " +
          this.saleOrderLineHistory?.lineCopiedFromHeaderId +
          "   Line " +
          this.saleOrderLineHistory?.lineCopiedFromLineNumberD365 +
          "   ";
      } else if (
        this.saleOrderLineHistory.lineCopiedFrom === EnumCopiedFrom.IsFromQuote
      ) {
        copiedInfo =
          " Proposal " +
          this.saleOrderLineHistory?.lineCopiedFromHeaderId +
          "   Line " +
          this.saleOrderLineHistory?.lineCopiedFromLineNumberD365 +
          "   ";
      }
    }
    return copiedInfo;
  }

  getSalesOrderId(
    saleOrderLineRequest: UpdateSalesOrderLineDto | SalesOrderLinesRequestDto
  ): string {
    if ("salesOrderId" in saleOrderLineRequest) {
      return saleOrderLineRequest.salesOrderId ?? "";
    } else if ("saleOrderId" in saleOrderLineRequest) {
      return saleOrderLineRequest.saleOrderId ?? "";
    }
    return "";
  }

  getFirstCurrency(
    saleOrderLineRequest: UpdateSalesOrderLineDto | SalesOrderLinesRequestDto
  ): string {
    if (Array.isArray(saleOrderLineRequest?.lines)) {
      return saleOrderLineRequest.lines[0]?.currencyCodeD365 ?? "";
    }
    return "";
  }

  getFirstCurrencyStatus(
    saleOrderLineRequest: UpdateSalesOrderLineDto | SalesOrderLinesRequestDto
  ): boolean {
    if (Array.isArray(saleOrderLineRequest?.lines)) {
      const obj =
        saleOrderLineRequest.lines[0]?.isnetInForeignCurrencyStatus ?? false;
      return obj;
    }
    return false;
  }

  checkCurrencyMismatch(
    saleOrderLineRequest: UpdateSalesOrderLineDto | SalesOrderLinesRequestDto,
    callFromMethod: string,
    lineAction: string
  ) {
    if (
      saleOrderLineRequest?.vendor &&
      (saleOrderLineRequest?.vendor?.vendorPoAssignment === "Default" ||
        saleOrderLineRequest?.vendor?.vendorPoAssignment === "Existing")
    ) {
      if (saleOrderLineRequest?.lines) {
        const checkCurrencyMismatchRequest: CheckCurrencyMismatchRequestDto = {
          currencyCode: this.getFirstCurrency(saleOrderLineRequest),
          purchaseOrderId: saleOrderLineRequest?.purchaseOrderId ?? "",
          salesOrderId: this.getSalesOrderId(saleOrderLineRequest),
          vendorDirectDelivery:
            saleOrderLineRequest?.vendor?.vendorDirectDelivery ?? "",
          vendorPoAssignment:
            saleOrderLineRequest?.vendor?.vendorPoAssignment ?? "",
          isnetInForeignCurrencyStatus:
            this.getFirstCurrencyStatus(saleOrderLineRequest) ?? false,
        };

        if (
          checkCurrencyMismatchRequest.isnetInForeignCurrencyStatus !== true
        ) {
          checkCurrencyMismatchRequest.isnetInForeignCurrencyStatus = false;
        }

        this._loaderService.setLoaderState(true);
        this._orderLineItem
          .checkCurrencyMismatch(checkCurrencyMismatchRequest)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              this._loaderService.setLoaderState(false);
              if (res.status.toLowerCase() == "success") {
                if (res?.data && res?.data?.isMismatch === true) {
                  this._toastr.error(
                    "Error",
                    res.message || "Currency must be the same"
                  );
                } else {
                  if (
                    callFromMethod ===
                    CheckCurrencyMismatchCallingMethod.onSubmitMethodCreateSaleOrderLine
                  ) {
                    this.onSubmitMethodcreateSalesOrderLine(
                      saleOrderLineRequest as unknown as SalesOrderLinesRequestDto
                    );
                  } else if (
                    callFromMethod ===
                    CheckCurrencyMismatchCallingMethod.onSubmitMethodUpdateSaleOrderLine
                  ) {
                    this.onSubmitMethodUpdateSalesOrderLine(
                      saleOrderLineRequest as unknown as UpdateSalesOrderLineDto
                    );
                  } else if (
                    callFromMethod ===
                    CheckCurrencyMismatchCallingMethod.nextMethodCreateSaleOrderLine
                  ) {
                    this.nextMethodCreateSaleOrderLine(
                      saleOrderLineRequest as unknown as SalesOrderLinesRequestDto,
                      lineAction
                    );
                  } else if (
                    callFromMethod ===
                    CheckCurrencyMismatchCallingMethod.nextMethodUpdateSaleOrderLine
                  ) {
                    this.nextMethodUpdateSaleOrderLine(
                      saleOrderLineRequest as unknown as UpdateSalesOrderLineDto
                    );
                  }
                }
              }
            },
            error: res => {
              this._loaderService.setLoaderState(false);
              this._toastr.error(
                "Error",
                res.message || "Error checking the currecny validation"
              );
            },
          });
      }
    } else {
      if (
        callFromMethod ===
        CheckCurrencyMismatchCallingMethod.onSubmitMethodCreateSaleOrderLine
      ) {
        this.onSubmitMethodcreateSalesOrderLine(
          saleOrderLineRequest as unknown as SalesOrderLinesRequestDto
        );
      } else if (
        callFromMethod ===
        CheckCurrencyMismatchCallingMethod.onSubmitMethodUpdateSaleOrderLine
      ) {
        this.onSubmitMethodUpdateSalesOrderLine(
          saleOrderLineRequest as unknown as UpdateSalesOrderLineDto
        );
      } else if (
        callFromMethod ===
        CheckCurrencyMismatchCallingMethod.nextMethodCreateSaleOrderLine
      ) {
        this.nextMethodCreateSaleOrderLine(
          saleOrderLineRequest as unknown as SalesOrderLinesRequestDto,
          lineAction
        );
      } else if (
        callFromMethod ===
        CheckCurrencyMismatchCallingMethod.nextMethodUpdateSaleOrderLine
      ) {
        this.nextMethodUpdateSaleOrderLine(
          saleOrderLineRequest as unknown as UpdateSalesOrderLineDto
        );
      }
    }
  }

  onSubmitMethodcreateSalesOrderLine(
    saleOrderLineRequest: SalesOrderLinesRequestDto
  ) {
    if (!this.isEdit || this.onSubmitDone) {
      this._loaderService.setLoaderState(true);
      this._orderLineItem
        .createSaleOrderLine(saleOrderLineRequest)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: async res => {
            this._loaderService.setLoaderState(false);
            if (
              res.status.toLowerCase() == "success" ||
              res.data.createSalesOrderLineResponseDto?.StatusCode == 399
            ) {
              this._toastr.success(
                "Success",
                res.message || "Line Item Added Successfully"
              );
              if (this.saleOrderModalStatus) {
                this.closeEvent.emit();
                return;
              }
              if (res.data) {
                if (this.isFromCdi) {
                  const url = `/cdi/cdi-resolution-summary/${this.cdiRecId}`;
                  this.route.navigate([url]);
                } else {
                  if (this.workflowId != 0 && this.workflowId != undefined) {
                    this.route.navigateByUrl("/production-workflow", {
                      state: {
                        workflowId: this.workflowId,
                        salesOrderId: this.saleOrderId,
                      },
                    });
                    this._sharedService.updateSampleWorkflow(0);
                    return;
                  }
                  if (
                    res.data.salesOrderLines?.length &&
                    res.data.salesOrderLines[0].salesOrderIdD365
                  ) {
                    const url = `/orders/order-summary/${res.data.salesOrderLines[0]?.salesOrderIdD365}`;
                    this.route.navigate([url], this.state);
                  } else {
                    if (
                      res.data.createSalesOrderLineResponseDto?.SalesOrderId
                    ) {
                      const url = `/orders/order-summary/${res.data.createSalesOrderLineResponseDto?.SalesOrderId}`;
                      this.route.navigate([url], this.state);
                    } else {
                      const url = `/orders/order-summary/${this.saleOrderId}`;
                      this.route.navigate([url], this.state);
                    }
                  }
                }
              }
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error adding line item"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error adding line item"
            );
          },
        });
    }
  }

  onSubmitMethodUpdateSalesOrderLine(
    saleOrderLineRequest: UpdateSalesOrderLineDto
  ) {
    if (!this.isEdit || this.onSubmitDone) {
      this._loaderService.setLoaderState(true);
      const isQuantityValid = this.isQuantityValid(
        String(saleOrderLineRequest.lines?.orderedPurchaseQuantityD365 ?? 0)
      );
      if (!isQuantityValid) {
        return;
      }
      this._orderLineItem
        .updateSaleOrderLine(saleOrderLineRequest)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() == "success") {
              //CustomEmitter
              this.updateSalesOrderLineDtoEvent.emit(saleOrderLineRequest);

              this._toastr.success(
                "Success",
                res.message || "Line Item Updated Successfully"
              );
              if (this.saleOrderModalStatus) {
                this.closeEvent.emit();
                return;
              }
              if (res.data) {
                if (this.isFromCdi) {
                  const url = `/cdi/cdi-resolution-summary/${this.cdiRecId}`;
                  this.route.navigate([url]);
                } else {
                  if (res.data.salesOrderLines?.salesOrderIdD365) {
                    const url = `/orders/order-summary/${res.data.salesOrderLines?.salesOrderIdD365}`;
                    this.route.navigate([url], this.state);
                  } else {
                    if (
                      res.data.updateSalesOrderLinesResponseDto?.SalesOrderId
                    ) {
                      const url = `/orders/order-summary/${res.data.updateSalesOrderLinesResponseDto?.SalesOrderId}`;
                      this.route.navigate([url], this.state);
                    } else {
                      const url = `/orders/order-summary/${this.saleOrderId}`;
                      this.route.navigate([url], this.state);
                    }
                  }
                }
              }
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error Updating Line Item"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error Updating Line Item"
            );
          },
        });
    }
  }

  nextMethodCreateSaleOrderLine(
    saleOrderLineRequest: SalesOrderLinesRequestDto,
    lineAction: string
  ) {
    if (!this.isEdit || this.onSubmitDone) {
      this._loaderService.setLoaderState(true);
      const isQuantityValid = this.isQuantityValid(
        String(saleOrderLineRequest.lines?.[0].orderedPurchaseQuantityD365 ?? 0)
      );
      if (!isQuantityValid) {
        return;
      }
      this._orderLineItem
        .createSaleOrderLine(saleOrderLineRequest)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);

            if (
              res.status.toLowerCase() == "success" ||
              res?.data?.createSalesOrderLineResponseDto?.StatusCode == 399
            ) {
              this._toastr.success(
                "Success",
                res.message || "line item added successfully"
              );

              if (lineAction === "Add Line") {
                this.quickEntyFieldStatus = "Add Line";
                this.orderLineNumber = 0;
                this.lineId = 0;
                this.isEdit = false;
                this.getSaleOrderData();
                this.clearSaleOrderLineForm();
                this.lineDetail.clearLineForm();
                this.shipTo.clearShipToAddressForm();
                this.details.clearInHandDateAndShipDate();
                this.lineDetail.disabledNetConversionCurrencyField();
                this.lineGridRefresh.emit();
              } else if (lineAction === "Copy Header") {
                this.orderLineNumber = 0;
                this.lineId = 0;
                this.isEdit = false;
                this.quickEntyFieldStatus = "Copy Header";
                this.getSaleOrderData();
                this.clearLineData();
                this.lineDetail.clearLineForm();
                this.lineDetail.disabledNetField();
                this.lineGridRefresh.emit();
              } else if (lineAction === "Copy Line") {
                this.orderLineNumber = 0;
                this.lineId = 0;
                this.isEdit = false;
                this.quickEntyFieldStatus = "Copy Line";
                this.getSaleOrderData();
                this.lineGridRefresh.emit();
              } else if (lineAction === "Add Setup") {
                this.orderLineNumber = 0;
                this.lineId = 0;
                this.isEdit = false;
                this.quickEntyFieldStatus = "Add Setup";
                this.getSaleOrderData();
                this.clearLineData();
                this.lineDetail.clearLineForm(false);
                this.lineDetail.disabledNetField();
                this.patchValueInSetup();
                this.lineGridRefresh.emit();
              } else if (lineAction === "Misc") {
                this.orderLineNumber = 0;
                this.lineId = 0;
                this.isEdit = false;
                this.quickEntyFieldStatus = "Misc";
                this.getSaleOrderData();
                this.clearLineData();
                this.lineDetail.clearLineForm();
                this.lineDetail.disabledNetField();
                this.patchValueInMisc();
                this.lineGridRefresh.emit();
              }
              if (this.orderLineNumber) {
                this.orderItemLineNumber = this.orderLineNumber ?? "1";
              } else {
                this.getLineNumber(this.saleOrderId);
              }
              this.unTouchForm();
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error adding line item"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error adding line item"
            );
          },
        });
    } else {
      const isQuantityValid = this.isQuantityValid(
        String(saleOrderLineRequest.lines?.[0].orderedPurchaseQuantityD365 ?? 0)
      );
      if (!isQuantityValid) {
        return;
      }
      if (lineAction === "Add Line") {
        this.quickEntyFieldStatus = "Add Line";
        this.orderLineNumber = 0;
        this.lineId = 0;
        this.isEdit = false;
        this.getSaleOrderData();
        this.clearSaleOrderLineForm();
        this.lineDetail.clearLineForm();
        this.shipTo.clearShipToAddressForm();
        this.details.clearInHandDateAndShipDate();
        this.lineDetail.disabledNetConversionCurrencyField();
        this.lineGridRefresh.emit();
      } else if (lineAction === "Copy Header") {
        this.orderLineNumber = 0;
        this.lineId = 0;
        this.isEdit = false;
        this.quickEntyFieldStatus = "Copy Header";
        this.getSaleOrderData();
        this.clearLineData();
        this.lineDetail.clearLineForm();
        this.lineDetail.disabledNetField();
        this.saveToPortal = true;
        this.lineGridRefresh.emit();
      } else if (lineAction === "Copy Line") {
        this.orderLineNumber = 0;
        this.lineId = 0;
        this.isEdit = false;
        this.quickEntyFieldStatus = "Copy Line";
        this.getSaleOrderData();
        this.saveToPortal = true;
        this.lineGridRefresh.emit();
      } else if (lineAction === "Add Setup") {
        this.orderLineNumber = 0;
        this.lineId = 0;
        this.isEdit = false;
        this.quickEntyFieldStatus = "Add Setup";
        this.getSaleOrderData();
        this.clearLineData();
        this.lineDetail.clearLineForm(false);
        this.lineDetail.disabledNetField();
        this.patchValueInSetup();
        this.saveToPortal = true;
        this.lineGridRefresh.emit();
        this.orderLineNumber = 0;
      } else if (lineAction === "Misc") {
        this.orderLineNumber = 0;
        this.lineId = 0;
        this.isEdit = false;
        this.quickEntyFieldStatus = "Misc";
        this.getSaleOrderData();
        this.clearLineData();
        this.lineDetail.clearLineForm();
        this.lineDetail.disabledNetField();
        this.patchValueInMisc();
        this.saveToPortal = true;
        this.lineGridRefresh.emit();
      }

      if (this.orderLineNumber) {
        this.orderItemLineNumber = this.orderLineNumber ?? 1;
      } else {
        this.getLineNumber(this.saleOrderId);
      }
      this.unTouchForm();
    }
  }

  nextMethodUpdateSaleOrderLine(saleOrderLineRequest: UpdateSalesOrderLineDto) {
    this._loaderService.setLoaderState(true);
    if (!this.isEdit || this.onSubmitDone) {
      //console.log('update');
      const isQuantityValid = this.isQuantityValid(
        String(saleOrderLineRequest.lines?.orderedPurchaseQuantityD365 ?? 0)
      );
      if (!isQuantityValid) {
        return;
      }
      this._orderLineItem
        .updateSaleOrderLine(saleOrderLineRequest)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() == "success") {
              this._toastr.success(
                "Success",
                res.message || "Line Item Updated Successfully"
              );

              if (res.data) {
                this.clearSaleOrderLineForm();
                this.lineDetail.clearLineForm();
                this.shipTo.clearShipToAddressForm();
                this.details.clearInHandDateAndShipDate();
                this.lineDetail.disabledNetConversionCurrencyField();
                this.unTouchForm();
                if (this.orderLineNumber) {
                  if (res?.data?.salesOrderLines) {
                    const saleOrderLineList =
                      res?.data?.salesOrderLines.lineNumberD365;
                    this.orderItemLineNumber = saleOrderLineList || 1;
                  }
                  if (this.saleOrderModalStatus) {
                    this.getLineNumber(this.saleOrderId);
                    this.orderLineNumber = 0;
                    this.lineId = 0;
                    this.getSaleOrderData();
                    return;
                  }
                  this.route.navigate([
                    "/orders/order-line-item/" + this.saleOrderId,
                  ]);
                } else {
                  this.getLineNumber(this.saleOrderId);
                  this.getSaleOrderData();
                }
              }
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error Updating Line Item"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error Updating Line Item"
            );
          },
        });
    }
  }

  checkShowHideFields() {
    if (this.lineStatusD365?.toLowerCase() === "delivered") {
      this.disabledEnabledFormFieldForDelivered();
    }

    if (this.lineStatusD365?.toLowerCase() === "open order") {
      // Check if its the partial line Task:11400
      if (this.deliverRemainderD365 > this.orderedPurchaseQuantityD365) {
        this.disabledEnabledFormFieldForPartiallyDeliveredLines();
        setTimeout(() => {
          this.netFieldStatus = true;
        }, 2000);
        this.disabledQuantityField();
      }
    }
  }

  disabledEnabledFormFieldForPartiallyDeliveredLines() {
    this.saleOrderItemLine
      .get(["vendor", "duplicateVendorDirectDelivery"])
      ?.disable();
    this.saleOrderItemLine.get(["vendor", "vendorPoAssignment"])?.disable();

    this.saleOrderItemLine.get(["lines", "factoryNotes"])?.disable();
    this.saleOrderItemLine.get(["lines", "itemNumberD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "externalItemNumberD365"])?.disable();
    this.saleOrderItemLine.get(["lines", "currencyCodeD365"])?.disable();
    this.saleOrderItemLine
      .get(["lines", "isnetInForeignCurrencyStatus"])
      ?.disable();

    this.saleOrderItemLine.get(["detail", "shippingMethod"])?.disable();
    this.saleOrderItemLine.get(["detail", "accountNumberD365"])?.disable();
    this.saleOrderItemLine.get(["detail", "firm"])?.disable();
    this.saleOrderItemLine.get(["detail", "shipDate"])?.disable();
    this.saleOrderItemLine.get(["detail", "inHandDate"])?.disable();

    this.saleOrderItemLine.get(["shipTo", "addressSelectionName"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "deliveryNameD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "attentionInformationD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "countryRegionIdD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "attention"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "locationD365"])?.disable();
    this.saleOrderItemLine.get(["shipTo", "rSMJNIShipToEmailD365"])?.disable();
    this.saleOrderItemLine
      .get(["shipTo", "shipToVendorAccountNameD365"])
      ?.disable();
    this.saleOrderItemLine.get(["shipTo", "addressFormat"])?.disable();

    this.saleOrderItemLine.get(["lines", "sell"])?.enable();
    this.saleOrderItemLine.get(["lines", "lineDescriptionD365"])?.enable();
    this.sellRateValueWithIconStatus = false; // to make it sell field enable

    this.netFieldStatus = true;

    this.clientShipToAddressDisabled = true;
    this.vendorNameShipToAddressDisabled = true;
    this.saleOrderItemLine.get(["lines", "salesClassificationD365"])?.disable();
  }

  currencyFromVendorEvent(currecny: string) {
    if (!currecny) {
      currecny = "USD";
    }

    if (currecny !== "USD") {
      this.lineDetail.onCurrencyEnable("checked");
    }
    this.saleOrderItemLine.patchValue({
      lines: {
        currencyCodeD365: currecny,
      },
    });
    if (this.lineDetail) {
      this.lineDetail.onCurrencyConversion();
    }
  }

  isQuantityValid($event: string) {
    if (this.salesOrderLine.lineStatusD365) {
      if (this.salesOrderLine.lineStatusD365.toLowerCase() === "delivered") {
        if (
          this.salesOrderLine.deliverRemainderD365 &&
          this.salesOrderLine.deliverRemainderD365 !== 0 &&
          this.salesOrderLine.orderedPurchaseQuantityD365
        ) {
          const matchedValue =
            this.salesOrderLine.orderedPurchaseQuantityD365 -
            this.salesOrderLine.deliverRemainderD365;
          if (Number($event) < matchedValue) {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              "The QTY cannot be less than the matched qty."
            );
            return false;
          }
        }
        if (
          this.salesOrderLine.deliverRemainderD365 === 0 &&
          (this.salesOrderLine.orderedPurchaseQuantityD365 ?? 0) >
            Number($event)
        ) {
          this._loaderService.setLoaderState(false);
          this._toastr.error(
            "Error",
            "The QTY cannot be less than the matched qty."
          );
          return false;
        }
      }
    }
    return true;
  }

  showHideButtons(proposalIdD365: string) {
    if (proposalIdD365 && proposalIdD365 != "") {
      this.isLineProposalExist = true;
    } else {
      this.isLineProposalExist = false;
    }
  }

  isMelissaValidationIgnoreEvent(value: boolean) {
    this.isMelissaValidationIgnore = value;
  }

  isMelissaAddressValidatedEvent(value: boolean) {
    if (value == true) {
      this.isCopiedLineMelissaValidationIgnore = false;
      this.isMelissaValidationIgnore = false;
    }
  }

  onSaleOrderNotesSave() {
    if (this.saleOrderId) {
      const obj: SalesOrderNotes = {
        salesOrderId: this.saleOrderId,
        portalKey: uuidv4(),
        description: "System Order # " + this.saleOrderId,
        id: 0,
        note: "Non approved address was accepted",
        purchaseOrderStatus: false,
        type: "Order note",
        purchaseOrderId: "",
        createdDate: "",
        createdBy: "",
        lastModifiedDate: null,
        lastModifiedBy: null,
        isActive: false,
      };

      this._orderNotesSidebarService
        .AddSalesOrderNotes(obj)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            // Console.log('nothing to do so far');
          },
        });
    }
  }

  getAllSalesClassifications() {
    this._programService
      .getAllSalesClassifications()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.salesClassificationList = res.data;
        },
      });
  }

  // isSalesClassificationRequired() {
  //   if (this.programId && this.programId != 0) {
  //     this._programService
  //       .getProgarmDetailsById(this.programId)
  //       .pipe(untilDestroyed(this))
  //       .subscribe({
  //         next: (res) => {
  //           if (res.data && res.data.dimensionD365) {
  //             this.isSalesClassificationIdRequired = true;
  //             this.saleOrderItemLine
  //               .get(['lines', 'salesClassificationD365'])
  //               ?.setValidators(Validators.required);
  //             this.saleOrderItemLine
  //               .get(['lines', 'salesClassificationD365'])
  //               ?.updateValueAndValidity();
  //           }
  //         }
  //       });
  //   }
  // }

  patchSalesClassficationValue(salesClassificationIdD365: number) {
    this.saleOrderItemLine.patchValue({
      lines: {
        salesClassificationD365: salesClassificationIdD365 ?? 0,
      },
    });
  }

  getD365DefaultProgramsAndSalesClassificationByClient(
    customerAccount: string
  ) {
    this._programService
      .getD365DefaultProgramsAndSalesClassificationByClient(customerAccount)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.status === "Success" && res?.data) {
            this.d365DefaultSalesClassification = res.data;
            if (
              this.d365DefaultSalesClassification
                ?.d365DefaultSalesClassificationCode
            ) {
              this.patchSalesClassficationValue(
                parseInt(
                  this.d365DefaultSalesClassification
                    ?.d365DefaultSalesClassificationCode ?? 0
                )
              );
              this.saleOrderItemLine
                .get(["lines", "salesClassificationD365"])
                ?.disable();
            }
          }
        },
      });
  }

  isVendorSelectedEvent(value: boolean) {
    if (this.lineDetail) {
      this.lineDetail.isVendorSelectedEvent(value);
    }
  }

  getPurchaseOrderId(
    saleOrderId: string,
    directDeliveryVendor: string
  ): Observable<boolean> {
    if (!saleOrderId || !directDeliveryVendor) {
      return of(false); // Immediately return false if inputs are invalid
    }

    const purchaseOrder: GetExtisingPOIdRequest = {
      SaleOrderId: saleOrderId,
      DirectDeliveryVendor: directDeliveryVendor,
    };

    return this._purchaseOrderSidebarService
      .GetExtisingPOId(purchaseOrder)
      .pipe(
        map(res => {
          if (
            res.data.PurchaseOrderIds &&
            res.data.PurchaseOrderIds.length > 0
          ) {
            this.purchaseOrderIdList = res.data.PurchaseOrderIds;
            this.displaySidebar = true;
            return true;
          }
          return false;
        }),
        catchError(err => {
          console.error("Error fetching Purchase Order IDs:", err);
          return of(false);
        }),
        untilDestroyed(this) // Ensures cleanup when component is destroyed
      );
  }

  ngAfterViewInit(): void {
    this.getSaleOrderData();
  }

  getSaleOrderDataFromAPI() {
    if (this.saleOrderId) this._loaderService.setLoaderState(true);
    this._orderLineItem
      .getSaleOrderData(this.saleOrderId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.data) {
            this.saleOrderData = res.data;
            this.getSaleOrderData();
          } else {
            this._toastr.error("Error", "Unable to Sales Order Details");
          }
        },
        error: () => {
          this._toastr.error("Error", "Unable to Sales Order Details");
        },
      });
  }

  closeSalesOrderLineModal() {
    this.closeSalesOrderLineEvent.emit();
  }
}
